/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './RoutesChart.scss';
import RoutePlanLeft from './RoutesChart/RoutePlanLeft';
import {
  ACTION_ROUTE,
  COLORS,
  DATE_BACKEND_FORMAT,
  FULL_DATE_FORMAT,
  ORDER_STATUSES,
  STEP_TYPE,
  ORDER_STATUSES_FAILED,
  ORDER_DELIVERY_STATUS_COMPLETED,
  ORDER_PICKUP_STATUS_COMPLETED,
  STEP_STATUS,
} from '../constants';
import {
  Badge,
  Button,
  DatePicker,
  Input,
  message,
  Modal,
  notification,
  Popconfirm,
  Popover,
  Spin,
  Select,
  Tooltip,
} from 'antd';
import {
  CloseOutlined,
  QuestionCircleOutlined,
  UndoOutlined,
  RedoOutlined,
  AimOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import range from 'lodash/range';
import findIndex from 'lodash/findIndex';
import cloneDeep from 'lodash/cloneDeep';
import { chain, sortBy, uniq, forEach } from 'lodash';
import dayjs from 'dayjs';
import { ReactSVG } from 'react-svg';
import { DragDropContext } from '@hello-pangea/dnd';
import DriverTime from './RoutesChart/DriverTime';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';

import {
  doDispatchRoutes,
  doGetLogGMPRO,
  doGetRoutesByDate,
  doOptimizeRoutesByDate,
  doOptimizeRoutesGmproByDate,
  doReorderOrdersForRoutes,
} from '../modules/routes/stores/thunk';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { selectAllRoutes } from '../modules/routes/stores/slice';
import { doSynchronizeSchedulerForm } from '../modules/scheduler-forms/stores/thunk';
import {
  calculatePixelBetweenStops,
  getArrivalTime,
  getFinishTime,
  getFirstStartAndLastEnd,
  getWidthChart,
  isDiffTwoArrayStops,
  sortSteps,
} from '../utils/common';
import DriverList from './RoutesChart/DriverList';
import RouteMap from './RoutesChart/RouteMap';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import UnScheduled from './RoutesChart/UnScheduled';
import { selectAllOrders } from '../modules/orders/stores/slice';
import {
  doChangeToDraft,
  doChangeToCancelled,
  doChangeToUnassigned,
  doDeleteOrders,
  doGetOrders,
} from '../modules/orders/stores/thunk';
import RouteOrdersTable from '../components/Tables/RouteOrdersTable';
import OrdersFilter from '../components/Forms/Filters/OrdersFilter';
import SwapStopsToDriver from './RoutesChart/SwapStopsToDriver';
import { setShouldFitbounds } from '../redux/slice';
import StopClicked from './RoutesChart/StopClicked';
import SearchStops from './RoutesChart/SearchStops';
import { doGetOrderTickets } from '../modules/order-tickets/stores/thunk';
import { socket } from '../utils/socket';
import { getOptimizeRun } from '../modules/routes/api';
import { stopMatchesFilter, isStopFilterEmpty, extractZip } from '../utils/stopFilter';
import { insertMovedLegs, isSameSelectedStep } from '../utils/multiSelect';
import { tenantConfig } from '../config/tenantConfig';
import { useTranslation } from 'react-i18next';

const initStateTimeline = {
  minTimePx: null,
  maxTimePx: null,
  maxTimeMinutes: null,
  minTimeMinutes: null,
};

const initMultipleSteps = {
  toDriverId: undefined,
  index: undefined,
  steps: [],
  visibleModal: false,
  searchedRoutes: [],
  selectedRoute: undefined,
  index: undefined,
};

const InitOneHourPx = 300;

// #125 — how the page follows an optimize / reorder run it started.
const RUN_POLL_FIRST_MS = 1500;
const RUN_POLL_EVERY_MS = 3000;
const RUN_POLL_MAX_MS = 10 * 60 * 1000;

const RoutesChart = () => {
  const { t } = useTranslation(['routes', 'common']);
  const { type } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = Object.fromEntries(searchParams);
  const [api, contextHolder] = message.useMessage();

  // Configure global notification placement and offset from top
  useEffect(() => {
    notification.config({ placement: 'topRight', top: 72 });
  }, []);

  const dateCookie = Cookies.get('date');

  const routes = useSelector(selectAllRoutes);
  const orders = useSelector(selectAllOrders);
  const orderTickets = useSelector((state) => state.orderTickets.orderTickets);

  const { isFetching: isFetchingShift } = useSelector((state) => state.schedulerForm);

  const { isFetching: isFetchingRoute, action: actionRoute, status: statusRoute } = useSelector((state) => state.route);

  const { isFetching: isFetchingOrder, action: actionOrder, status: statusOrder } = useSelector((state) => state.order);

  // Resolve the date SYNCHRONOUSLY (query → cookie → today). It used to start
  // undefined and be set by an effect, so the mount fired getAllDataForPage
  // for the cookie date first and the URL date right after — two overlapping
  // /orders fetches whose responses raced (BUT direct-send incident, Aug 26
  // 2026: Sofia saw Sep-4 orders as Unscheduled on the Aug-27 plan).
  const [date, setDate] = useState(() => {
    if (process.env.REACT_APP_DEMO_MODE === 'true') return dayjs('2026-02-06', DATE_BACKEND_FORMAT);
    const initial = new URLSearchParams(window.location.search).get('date') || Cookies.get('date');
    const parsed = initial ? dayjs(initial, DATE_BACKEND_FORMAT) : null;
    return parsed && parsed.isValid() ? parsed : dayjs();
  });
  const [oneHourPx, setOneHourPx] = useState(InitOneHourPx);
  const [isDragging, setIsDragging] = useState(false);
  const [solutions, setSolutions] = useState({});
  const [originalSolutions, setOriginalSolutions] = useState({});
  // OVO-208 — client-side undo/redo of the drag-drop arrangement. The plan
  // lives in `solutions`; each rearrangement snapshots the previous state.
  // Restore-only: the operator persists via the existing Apply/Optimise step.
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const solutionsRef = useRef(solutions);
  const lastPreOptimizeRef = useRef(null);
  const [mapData, setMapData] = useState({});
  const [startTime, setStartTime] = useState('08:00');
  const [hourStart, setHourStart] = useState(8);
  const [hourEnd, setHourEnd] = useState(8);
  const [stateTimeLine, setStateTimeLine] = useState(initStateTimeline);
  const [ordersSelected, setOrdersSelected] = useState([]);
  // OVO-026 — local UI state for the route-plan Orders subview's toolbar
  // (search + filter drawer + column-config gear). Filtering happens client-
  // side against the already-loaded `orders` array; the data fetch stays
  // scoped to the URL `?date=` value as before.
  const [routeOrdersSearch, setRouteOrdersSearch] = useState('');
  const [routeOrdersSearchInput, setRouteOrdersSearchInput] = useState('');
  const [isRouteOrdersFilterOpen, setIsRouteOrdersFilterOpen] = useState(false);
  const [routeOrdersFilterQuery, setRouteOrdersFilterQuery] = useState({});
  const [isRouteOrdersActionOpen, setIsRouteOrdersActionOpen] = useState(false);
  const routeOrdersConfigRef = useRef({});
  const [isOptimize, setIsOptimize] = useState(false);
  // #125 — the optimize / reorder run this page is waiting for ({ id,
  // startedAt }). The socket event is the fast path; polling the run makes
  // sure the page settles even when the event never arrives.
  const pendingRunRef = useRef(null);
  const runPollTimerRef = useRef(null);
  const settledRunIdsRef = useRef(new Set());
  const [selectedStep, setSelectedStep] = useState({});
  const [multipleStepState, setMultipleStepState] = useState(initMultipleSteps);
  const [hideDriverIds, setHideDriverIds] = useState([]);
  const [dragDriverIds, setDragDriverIds] = useState([]);
  const [allStops, setAllStops] = useState([]);
  const [searchStopsMap, setSearchStopsMap] = useState('');
  // Advanced filter (Sep 2026) — explicit partner + zip selection behind the
  // filter-icon toggle, AND-combined with the text above. All three live in
  // ONE `stopFilter` object read by the map pins, the unscheduled list and the
  // optimize set (see utils/stopFilter.js — the surfaces must never drift).
  const [advancedFilterOpen, setAdvancedFilterOpen] = useState(false);
  const [filterPartnerIds, setFilterPartnerIds] = useState([]);
  const [filterZips, setFilterZips] = useState([]);
  const stopFilter = useMemo(
    () => ({ query: searchStopsMap, partnerIds: filterPartnerIds, zips: filterZips }),
    [searchStopsMap, filterPartnerIds, filterZips],
  );
  const hasStopFilter = !isStopFilterEmpty(stopFilter);
  const advancedCriteriaCount = (filterPartnerIds.length ? 1 : 0) + (filterZips.length ? 1 : 0);
  const clearStopFilter = () => {
    setSearchStopsMap('');
    setFilterPartnerIds([]);
    setFilterZips([]);
  };

  // ─── Draggable separator between map and chart ──────────────────────
  const [chartHeight, setChartHeight] = useState(200);
  const isDraggingSeparator = useRef(false);
  const startY = useRef(0);
  const startHeight = useRef(200);
  const wrapperRef = useRef(null);
  // The horizontal/vertical scroll container for the timeline body (`.scrollCustom
  // .tbody`). Was referenced by the zoom-scroll-restore + range-fit effects but
  // never declared (a latent dead ref — its branch rarely ran). Now wired up.
  const containerTimelineRef = useRef(null);

  const onSeparatorMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      isDraggingSeparator.current = true;
      startY.current = e.clientY;
      startHeight.current = chartHeight;
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
    },
    [chartHeight],
  );

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDraggingSeparator.current) return;
      // Dragging down = shrink chart, dragging up = grow chart
      const delta = startY.current - e.clientY;
      const newHeight = Math.min(Math.max(startHeight.current + delta, 100), 600);
      setChartHeight(newHeight);
    };
    const onMouseUp = () => {
      if (!isDraggingSeparator.current) return;
      isDraggingSeparator.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);
  // ──────────────────────────────────────────────────────────────────

  const isFetching = isFetchingShift || isFetchingOrder || isOptimize || isFetchingRoute;

  // How many unscheduled ORDERS match the filter. Counted by orderCode — a
  // STANDARD unscheduled order is two pseudo-steps (pickup + delivery), so the
  // old step count announced "8 orders" for 4. Order-level OR: an order counts
  // when ANY of its legs matches (the rule the list + the optimize set apply).
  const unscheduledSteps = solutions['unscheduled'] || [];
  const totalUnscheduled = useMemo(() => new Set(unscheduledSteps.map((s) => s.orderCode)).size, [unscheduledSteps]);
  const matchedUnscheduled = useMemo(() => {
    if (!hasStopFilter) return totalUnscheduled;
    return new Set(unscheduledSteps.filter((step) => stopMatchesFilter(step, stopFilter)).map((s) => s.orderCode)).size;
  }, [unscheduledSteps, stopFilter, hasStopFilter, totalUnscheduled]);
  const isFilterActive = hasStopFilter && matchedUnscheduled !== totalUnscheduled;

  // Advanced-filter option lists, derived from TODAY's UNSCHEDULED stops only
  // (Diego, Sep 7 2026 — built from every stop, the lists offered partners and
  // zips that had nothing left to plan). Count = unscheduled orders (what the
  // filter scopes for Optimize). A selection carried over from another day
  // keeps the label it was picked with, so a chip never degrades to a bare id.
  const partnerLabelRef = useRef(new Map());
  const zipLabelRef = useRef(new Map());
  const { partnerOptions, zipOptions } = useMemo(() => {
    const partners = new Map();
    const zips = new Map();
    const unschedByPartner = new Map();
    const unschedByZip = new Map();
    // #174 (Oct 26 2026) — the lists come from EVERY stop of the day again (CT
    // plans almost nothing "unscheduled": its tournées are generated), each
    // option showing "to plan / total" orders. Optimize still takes only the
    // matching unscheduled orders.
    const totalByPartner = new Map();
    const totalByZip = new Map();
    const addTotal = (map, key, s) => {
      if (!map.has(key)) map.set(key, new Set());
      const ids = Array.isArray(s.orderIds) && s.orderIds.length ? s.orderIds : [s.orderId ?? s.orderCode];
      ids.forEach((id) => map.get(key).add(String(id)));
    };
    (allStops || []).forEach((s) => {
      const isUnsched = s.routeId === 'unscheduled';
      if (s.partnerId != null) {
        const key = String(s.partnerId);
        if (!partners.has(key))
          partners.set(key, { id: key, name: s.partnerName || s.partner || key, code: s.partnerCode || '' });
        addTotal(totalByPartner, key, s);
        if (isUnsched) {
          if (!unschedByPartner.has(key)) unschedByPartner.set(key, new Set());
          unschedByPartner.get(key).add(s.orderCode);
        }
      }
      const z = extractZip(s.address);
      if (z?.zip) {
        if (!zips.has(z.zip)) zips.set(z.zip, { zip: z.zip, localities: new Map() });
        if (z.locality) {
          const loc = zips.get(z.zip).localities;
          loc.set(z.locality, (loc.get(z.locality) || 0) + 1);
        }
        addTotal(totalByZip, z.zip, s);
        if (isUnsched) {
          if (!unschedByZip.has(z.zip)) unschedByZip.set(z.zip, new Set());
          unschedByZip.get(z.zip).add(s.orderCode);
        }
      }
    });
    const partnerOpts = [...partners.values()]
      .sort((a, b) => String(a.name).localeCompare(String(b.name), 'fr', { sensitivity: 'base' }))
      .map((p) => {
        const label = p.code ? `${p.code} — ${p.name}` : String(p.name);
        partnerLabelRef.current.set(p.id, label);
        return {
          value: p.id,
          label,
          count: unschedByPartner.get(p.id)?.size || 0,
          total: totalByPartner.get(p.id)?.size || 0,
        };
      });
    filterPartnerIds.forEach((id) => {
      if (!partners.has(String(id)))
        partnerOpts.push({ value: String(id), label: partnerLabelRef.current.get(String(id)) || String(id), count: 0 });
    });
    const zipOpts = [...zips.values()]
      .sort((a, b) => a.zip.localeCompare(b.zip))
      .map((z) => {
        // The most frequent locality spelling seen for that zip today.
        const locality = [...z.localities.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
        const label = locality ? `${z.zip} · ${locality}` : z.zip;
        zipLabelRef.current.set(z.zip, label);
        return {
          value: z.zip,
          label,
          count: unschedByZip.get(z.zip)?.size || 0,
          total: totalByZip.get(z.zip)?.size || 0,
        };
      });
    filterZips.forEach((zip) => {
      if (!zips.has(zip)) zipOpts.push({ value: zip, label: zipLabelRef.current.get(zip) || zip, count: 0 });
    });
    return { partnerOptions: partnerOpts, zipOptions: zipOpts };
  }, [allStops, filterPartnerIds, filterZips]);

  // Option row: label + "to plan / total" orders (#174).
  const renderFilterOption = (opt) => (
    <span className="stop-filter-option">
      <span className="lbl">{opt.label}</span>
      {opt.data?.total ? (
        <span className="cnt" title={t('filterOptionCountHint')}>
          {opt.data.count} / {opt.data.total}
        </span>
      ) : null}
    </span>
  );

  // One-line description of the whole filter for the banner ("epi" · Partners:
  // ET, BEAN · Zip: 1202) — partner chips show the CODE, like everywhere else.
  const filterSummary = useMemo(() => {
    const parts = [];
    if (searchStopsMap) parts.push(`"${searchStopsMap}"`);
    if (filterPartnerIds.length)
      parts.push(
        `${t('partners')}: ${filterPartnerIds
          .map((id) => (partnerLabelRef.current.get(String(id)) || String(id)).split(' — ')[0])
          .join(', ')}`,
      );
    if (filterZips.length) parts.push(`${t('zipCodes')}: ${filterZips.join(', ')}`);
    return parts.join(' · ');
  }, [searchStopsMap, filterPartnerIds, filterZips, t]);

  const ordersInDate = orders.filter(
    (order) =>
      date &&
      dayjs(order.deliveryDate).format(DATE_BACKEND_FORMAT) === date?.format(DATE_BACKEND_FORMAT) &&
      ![ORDER_STATUSES.DRAFT, ORDER_STATUSES.GEOCODING, ORDER_STATUSES.ERROR].includes(order.status),
  );

  const getAllDataForPage = () => {
    if (!date) return;
    dispatch(doGetRoutesByDate({ date: date?.format(DATE_BACKEND_FORMAT) }));
    dispatch(doGetLogGMPRO({ date: date?.format(DATE_BACKEND_FORMAT) }));
    dispatch(
      doGetOrders({
        deliveryDateFrom: date?.format(DATE_BACKEND_FORMAT),
        deliveryDateTo: date?.format(DATE_BACKEND_FORMAT),
        take: 1000000,
      }),
    );
    setIsOptimize(false);
  };

  useEffect(() => {
    if (type) {
      setOrdersSelected([]);
      setSelectedStep({});
      setSearchStopsMap('');
    }
  }, [type]);

  useMemo(() => {
    getAllDataForPage();
    dispatch(doGetOrderTickets());
  }, [date]);

  useEffect(() => {
    // In demo mode, always use the date that has mock data
    const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
    const demoDate = '2026-02-06';
    // Same-day updates keep the previous dayjs identity so this effect can
    // never retrigger the [date]-keyed fetches with a duplicate request.
    const applyDate = (d) => setDate((prev) => (prev && prev.isSame(d, 'day') ? prev : d));

    if (isDemoMode) {
      applyDate(dayjs(demoDate, DATE_BACKEND_FORMAT));
      Cookies.set('date', demoDate);
      if (!query?.date || query?.date !== demoDate) {
        setSearchParams({ date: demoDate });
      }
    } else if (query?.date) {
      applyDate(dayjs(query?.date, DATE_BACKEND_FORMAT));
      Cookies.set('date', query?.date);
    } else if (dateCookie) {
      applyDate(dateCookie ? dayjs(dateCookie, DATE_BACKEND_FORMAT) : dayjs());
    } else {
      applyDate(dayjs());
      Cookies.set('date', dayjs().format(DATE_BACKEND_FORMAT));
    }
  }, [query?.date, dateCookie]);

  useEffect(() => {
    if (actionOrder === 'changeToUnassigned' && statusOrder === 'succeeded') {
      type === 'add-orders'
        ? navigate(`/admin/route-plans?date=${date?.format(DATE_BACKEND_FORMAT)}`)
        : dispatch(
            doGetOrders({
              deliveryDateFrom: date?.format(DATE_BACKEND_FORMAT),
              deliveryDateTo: date?.format(DATE_BACKEND_FORMAT),
              take: 1000000,
            }),
          );
      setOrdersSelected([]);
    }
    if (actionOrder === 'changeToDraft' && statusOrder === 'succeeded') {
      dispatch(
        doGetOrders({
          deliveryDateFrom: date?.format(DATE_BACKEND_FORMAT),
          deliveryDateTo: date?.format(DATE_BACKEND_FORMAT),
          take: 1000000,
        }),
      );
      setOrdersSelected([]);
    }
  }, [actionOrder, statusOrder]);

  useEffect(() => {
    if (actionRoute === 'optimizeByDate' && statusRoute === 'failed' && isOptimize) {
      setIsOptimize(false);
    }
  }, [actionRoute, statusRoute]);

  // ─── #125 — follow an optimize / reorder run to its end ────────────────
  //
  // The page used to leave "Loading…" only when the socket.io broadcast of the
  // optimizer callback reached it, and never did when the callback and this
  // tab's socket sat on different Cloud Run instances (prod, Sep 29 2026).
  // Optimize / reorder now answer with the run to follow; whichever of the
  // socket event and the poll reports its end first settles the page.
  const stopRunPoll = () => {
    clearTimeout(runPollTimerRef.current);
    runPollTimerRef.current = null;
  };

  const settleRun = (runId, outcome, message) => {
    if (runId != null) {
      if (settledRunIdsRef.current.has(runId)) return;
      settledRunIdsRef.current.add(runId);
    }
    if (runId == null || pendingRunRef.current?.id === runId) {
      pendingRunRef.current = null;
      stopRunPoll();
    }
    getAllDataForPage(); // also clears the spinner
    if (outcome === 'failed') {
      notification.error({ message: t('optimizeFailed'), description: message });
    } else if (outcome === 'timeout') {
      notification.warning({ message: t('optimizeNoResult'), description: t('optimizeNoResultDesc') });
    }
  };

  const pollRun = (runId) => {
    const tick = async () => {
      const pending = pendingRunRef.current;
      if (pending?.id !== runId) return;
      const { response, error } = await getOptimizeRun(runId);
      if (pendingRunRef.current?.id !== runId) return; // settled meanwhile
      if (response?.status === 'COMPLETED' || error?.statusCode === 404) {
        settleRun(runId, 'completed');
      } else if (response?.status === 'FAILED') {
        settleRun(runId, 'failed', response.message);
      } else if (Date.now() - pending.startedAt > RUN_POLL_MAX_MS) {
        settleRun(runId, 'timeout');
      } else {
        runPollTimerRef.current = setTimeout(tick, RUN_POLL_EVERY_MS);
      }
    };
    runPollTimerRef.current = setTimeout(tick, RUN_POLL_FIRST_MS);
  };

  // `result` is what optimize / reorder answered: { runId, runStatus }.
  // Returns false when there is no run to follow (a backend without run
  // tracking): the socket event alone decides, as before.
  const followRun = (result) => {
    if (result?.runStatus === 'COMPLETED') {
      // Nothing had to go to the optimizer: the change is already saved.
      settleRun(null, 'completed');
      return true;
    }
    const runId = result?.runId;
    if (!runId) return false;
    if (settledRunIdsRef.current.has(runId)) return true; // the event beat the answer
    stopRunPoll();
    pendingRunRef.current = { id: runId, startedAt: Date.now() };
    // Another dispatcher's run ending meanwhile reloaded the page and cleared
    // the spinner: this tab still waits for its own.
    setIsOptimize(true);
    pollRun(runId);
    return true;
  };

  useEffect(() => {
    // OVO-005 — stale `routePlan` listeners used to pile up.
    //
    // The previous version registered an anonymous handler with `socket.on(...)`
    // and on cleanup called `socket.disconnect()`. `socket.disconnect()` does
    // NOT remove listeners — it only closes the connection — so every time the
    // operator changed the selected day, a NEW handler was added while every
    // OLD handler (with its date captured by closure) stayed alive on the
    // shared socket. A subsequent `optimizing.completed` notification for a
    // previously-viewed day matched a stale handler and called
    // `getAllDataForPage()` closed over that stale date, replacing the
    // current day's data in Redux with the wrong day's. The view appeared
    // to "jump back" to a day the operator had moved away from.
    //
    // Fix:
    //   - Use a NAMED handler so we can `socket.off(...)` it on cleanup.
    //   - Drop `socket.disconnect()` — the socket is a shared singleton
    //     imported from utils/socket.js; disconnecting it from one component
    //     would be hostile to any other consumer (e.g. Orders.jsx).
    //   - Hard-gate every event on date match before doing anything.
    //
    // E2E coverage: e2e/tests/ovo-005-day-plan-no-jump.spec.ts.
    const dateFormat = date?.format(DATE_BACKEND_FORMAT);
    const handleRoutePlan = (data) => {
      if (data?.payload?.name !== dateFormat) return; // wrong day — ignore
      const runId = data.payload.runId ?? null;
      // #125 — while this tab waits for its own run, any other end (another
      // dispatcher's run, a driver-side refresh) is skipped: our run's end
      // reloads the page anyway.
      if (pendingRunRef.current && runId !== pendingRunRef.current.id) return;
      if (data.event === 'optimizing.completed') {
        console.log('routePlan.optimizing.completed');
        settleRun(runId, 'completed');
      } else if (data.event === 'optimizing.error') {
        console.log('routePlan.optimizing.error');
        // top offset of the notification is controlled globally via notification.config in this component
        settleRun(runId, 'failed', data.payload.message);
      }
    };
    socket.connect();
    socket.on('routePlan', handleRoutePlan);
    return () => {
      socket.off('routePlan', handleRoutePlan);
      // A run followed for this day is no longer this page's business.
      stopRunPoll();
      pendingRunRef.current = null;
    };
  }, [date]);

  // OVO-208 — keep a live ref to `solutions` so the snapshot helpers always
  // read the latest committed arrangement (no stale closure).
  useEffect(() => {
    solutionsRef.current = solutions;
  }, [solutions]);

  // Snapshot the current arrangement, then apply the next one (drag / move).
  const commitSolutions = (next) => {
    setUndoStack((s) => [...s, cloneDeep(solutionsRef.current)]);
    setRedoStack([]);
    setSolutions(next);
  };
  // After an undo/redo, mark every route dirty so the "Apply" button appears
  // and the existing reorder persists the restored arrangement.
  const markAllRoutesDirty = () => {
    setDragDriverIds((routes || []).map((r) => r?.driver?.id).filter((id) => id != null));
  };
  const handleUndo = () => {
    if (!undoStack.length) return;
    const prev = undoStack[undoStack.length - 1];
    setRedoStack((r) => [...r, cloneDeep(solutionsRef.current)]);
    setUndoStack((s) => s.slice(0, -1));
    setSolutions(cloneDeep(prev));
    markAllRoutesDirty();
  };
  const handleRedo = () => {
    if (!redoStack.length) return;
    const next = redoStack[redoStack.length - 1];
    setUndoStack((s) => [...s, cloneDeep(solutionsRef.current)]);
    setRedoStack((r) => r.slice(0, -1));
    setSolutions(cloneDeep(next));
    markAllRoutesDirty();
  };

  const handleSynchronize = async () => {
    await dispatch(doSynchronizeSchedulerForm({ date: date?.format(DATE_BACKEND_FORMAT) }));
    getAllDataForPage();
  };

  const handleOptimize = async () => {
    setIsOptimize(true);
    await dispatch(doOptimizeRoutesByDate({ date: date?.format(DATE_BACKEND_FORMAT) }));
  };

  // #38 — Optimize places the unscheduled orders on routes without stops and
  // leaves built routes alone; `reoptimizeAll` (confirmed in the UI) rebuilds them.
  const handleOptimizeGmpro = async ({ reoptimizeAll = false } = {}) => {
    // OVO-208 — remember the manual arrangement so an undo after the optimise
    // completes restores it (seeded into the undo stack on the routes reload).
    lastPreOptimizeRef.current = cloneDeep(solutions);
    setIsOptimize(true);
    let ignoreOrders = [];
    if (hasStopFilter) {
      const unscheduleSteps = originalSolutions['unscheduled'];
      const matches = unscheduleSteps
        .filter((step) => stopMatchesFilter(step, stopFilter))
        .map((step) => step.orderCode);

      const orderCodesIgnore = unscheduleSteps
        .filter((step) => !matches.includes(step.orderCode))
        .map((step) => step.orderCode);

      ignoreOrders = [...new Set([...ignoreOrders, ...orderCodesIgnore])];
    }
    const resultAction = await dispatch(
      doOptimizeRoutesGmproByDate({
        date: date?.format(DATE_BACKEND_FORMAT),
        hideDriverIds: hideDriverIds?.map((id) => id.toString()),
        ignoreOrders,
        ...(reoptimizeAll ? { reoptimizeAll: true } : {}),
      }),
    );
    // A refusal clears the spinner through the failed-status effect (and
    // AppError shows why); a started run is followed to its end (#125).
    if (doOptimizeRoutesGmproByDate.fulfilled.match(resultAction)) {
      followRun(resultAction.payload);
    }
  };

  const handleDispatch = () => {
    // OVO-162 — only dispatch routes that haven't been dispatched yet. The
    // button used to resubmit EVERY route with stops, so each click re-emailed
    // every already-dispatched driver (10 dispatches → 10 mails each). The
    // backend has a matching skip-if-dispatched guard; this also avoids the
    // duplicate `order.dispatched` partner-webhook on unchanged routes.
    const routesToDispatchIds = routes
      .filter((route) => route.steps.length > 0 && !route.dispatched)
      ?.map((route) => route.id);
    dispatch(doDispatchRoutes({ ids: routesToDispatchIds }));
  };

  const getSummaryRoutes = () => {
    let stepsDone = 0;
    let stepsSkipped = 0;
    let stepsFailed = 0;
    let stepsTotal = 0;
    let workingMins = 0;
    let distance = 0;
    const totalOrders = [];
    routes?.forEach((route) => {
      if (hideDriverIds?.includes(route.driver.id)) return;
      route.steps
        ?.filter((step) => step.type !== STEP_TYPE.driver)
        .forEach((step) => {
          // Handle both single orderId and multiple orderIds
          const orderIds = step.orderIds || (step.orderId ? [step.orderId] : []);
          orderIds.forEach((orderId) => {
            const order = orders.find((o) => o.id === orderId);
            if (order) {
              totalOrders.push(order);

              if (step.order?.status === 'SKIPPED') {
                stepsSkipped += 1;
              } else if (step.order && ORDER_STATUSES_FAILED.includes(step.order.status)) {
                stepsFailed += 1;
              } else if (step.type === STEP_TYPE.delivery && ORDER_DELIVERY_STATUS_COMPLETED.includes(order?.status)) {
                stepsDone += 1;
              } else if (
                step.type === STEP_TYPE.pickup &&
                ORDER_PICKUP_STATUS_COMPLETED.includes(order?.pickupStatus)
              ) {
                stepsDone += 1;
              }
              stepsTotal += 1;
              workingMins += step.workingMins;
              distance += step.distance;
            }
          });
        });
    });

    return { stepsDone, stepsTotal, workingMins, distance, stepsSkipped, stepsFailed, totalOrders: totalOrders.length };
  };

  const summaryRoutes = getSummaryRoutes();

  const handleReorder = async () => {
    const drivers = [];
    const unscheduled = [];
    const allOrderIds = [];
    routes.map((route) => {
      const isDiff = isDiffTwoArrayStops(solutions[route.driver.id], originalSolutions[route.driver.id]);
      if (isDiff) {
        const driver = { driverId: route.id, steps: [] };
        solutions[route.driver.id].map((step) => {
          // SERVICE steps travel as the order's single `_DELIVERY` visit in
          // the reorder DTO (backend convention). Before this normalization
          // they matched NEITHER leg condition below and silently dropped
          // out of every submission (dev phantom report, Aug 18 2026).
          const legType = step.type.toUpperCase() === 'SERVICE' ? 'DELIVERY' : step.type.toUpperCase();
          if (legType === 'PICKUP') {
            step.orderIds.forEach((id) => {
              const order = orders.find((order) => order.id === id);

              if (order && order.status != 'SKIPPED' && order.pickupStatus != 'COLLECTED') {
                driver.steps.push(`${order.id}_PICKUP`);
                allOrderIds.push(order.id);
              }
            });
          } else {
            // Delivery step - handle multiple orderIds
            step.orderIds?.forEach((orderId) => {
              const order = orders.find((order) => order.id === orderId);

              if (order && order.status != 'SKIPPED' && order.status != 'COMPLETED') {
                driver.steps.push(`${order.id}_DELIVERY`);
                allOrderIds.push(order.id);
              }
            });
          }
        });
        drivers.push(driver);
      }
    });
    if (drivers.length === 0) return;

    const isDiffUnscheduled = isDiffTwoArrayStops(solutions['unscheduled'], originalSolutions['unscheduled']);
    if (isDiffUnscheduled) {
      solutions['unscheduled'].map((step) => {
        const order = orders.find((order) => step.orderIds.includes(order.id));
        // SERVICE pseudo-steps count as their delivery leg (single visit).
        const legType = step.type.toUpperCase() === 'SERVICE' ? 'DELIVERY' : step.type.toUpperCase();
        if (
          order &&
          order.status != 'SKIPPED' &&
          ((legType === 'PICKUP' && order.pickupStatus != 'COLLECTED') ||
            (legType === 'DELIVERY' && order.status != 'COMPLETED'))
        ) {
          unscheduled.push(order.id);
          allOrderIds.push(order.id);
        }
      });
    }
    // #125 — the spinner is safe again now that the run is followed to its end
    // (it used to hang whenever the socket event was lost). It also stops the
    // same arrangement from being applied again while the optimizer works.
    setIsOptimize(true);

    console.log('Reorder orders for routes', {
      date: date?.format(DATE_BACKEND_FORMAT),
      drivers,
      unscheduled,
      allOrderIds,
    });

    const resultAction = await dispatch(
      doReorderOrdersForRoutes({
        date: date?.format(DATE_BACKEND_FORMAT),
        routes: drivers,
        unscheduled: uniq(unscheduled),
        allOrderIds: uniq(allOrderIds),
      }),
    );

    // If the backend refused the reorder (most commonly: the route was already
    // dispatched/started by the time we applied — a stale client that hadn't
    // re-fetched), the optimistic move above is now lying. Re-fetch the
    // authoritative route state to revert it and tell the operator why.
    if (doReorderOrdersForRoutes.rejected.match(resultAction)) {
      setIsOptimize(false);
      api.open({
        type: 'error',
        content: resultAction.payload?.message || t('reorderFailed'),
        style: { marginTop: '52px' },
      });
      dispatch(doGetRoutesByDate({ date: date?.format(DATE_BACKEND_FORMAT) }));
      return;
    }
    if (!followRun(resultAction.payload)) setIsOptimize(false);
  };

  const moveSteps = () => {
    const updatedSolutions = cloneDeep(solutions);
    let moveIndex = multipleStepState.index;
    let targetDriverId = multipleStepState.toDriverId;
    const sortedAndGroupedData = chain(multipleStepState.steps || [])
      .sortBy('orderNumber')
      .groupBy('driverId')
      .value();

    if (typeof moveIndex !== 'number') {
      targetDriverId = targetDriverId === 'unscheduled' ? targetDriverId : targetDriverId;
      moveIndex = 0;
    }
    const moveAfterStep = { ...updatedSolutions[targetDriverId][moveIndex] };
    Object.keys(sortedAndGroupedData).forEach((fromDriverId) => {
      let sourceClone = [...updatedSolutions[fromDriverId]];

      const stepsNeedMove = sortBy(sortedAndGroupedData[fromDriverId], 'type');
      let stepsToMove = [];

      stepsNeedMove.forEach((step) => {
        if (step.type === STEP_TYPE.pickup) {
          const stepPushPickup = sourceClone.find(
            (stepSc) => step.id === stepSc.id && stepSc.type === STEP_TYPE.pickup,
          );
          const stepsPush = [stepPushPickup];
          const orderIds = step.orderIds;

          orderIds.forEach((id) => {
            stepsPush.push(sourceClone.find((step) => step.orderId === id && step.type === STEP_TYPE.delivery));
          });
          stepsToMove = [...stepsToMove, ...stepsPush];
        } else {
          const stepPushDelivery = sourceClone.find(
            (stepSc) => step.id === stepSc.id && stepSc.type === STEP_TYPE.delivery,
          );

          stepsToMove.push(stepPushDelivery);

          const stepPickup = sourceClone.find(
            (stepSc) => stepSc.orderIds?.includes(stepPushDelivery.orderId) && stepSc.type === STEP_TYPE.pickup,
          );
          stepsToMove.push({
            ...stepPickup,
            id: stepPickup.orderIds.length > 1 ? uuidv4() : stepPickup.id,
            orderIds: [stepPushDelivery.orderId],
          });

          if (stepPushDelivery.orderIds.length > 1) {
            sourceClone = sourceClone.map((sc) => {
              if (stepPickup.id === sc.id) {
                return {
                  ...sc,
                  orderIds: sc.orderIds.filter((id) => id !== stepPushDelivery.orderId),
                };
              }
              return sc;
            });
          }
        }
      });

      sourceClone = sourceClone.filter((step) => {
        return !stepsToMove.some((moveStep) => step.id === moveStep.id && step.type === moveStep.type);
      });

      const targetClone = fromDriverId === targetDriverId ? [...sourceClone] : [...updatedSolutions[targetDriverId]];

      moveIndex = targetClone.findIndex((step) => step.id === moveAfterStep.id && step.type === moveAfterStep.type);

      const pushData = sortBy(stepsToMove, 'type');

      pushData.reverse().forEach((step) => {
        moveIndex++;
        targetClone.splice(moveIndex, 0, {
          ...step,
          marginLeft: 8,
        });
      });

      updatedSolutions[targetDriverId] = targetClone;

      if (fromDriverId !== targetDriverId) {
        updatedSolutions[fromDriverId] = sourceClone;
      }
    });

    commitSolutions(updatedSolutions);
    setMultipleStepState(initMultipleSteps);
  };

  // #129 — a selected badge is identified by its own `id` (unique per badge:
  // `<stepId>-<TYPE>` on a route, `<orderCode>-pickup|delivery` in Unscheduled).
  // It used `stepId`, which is null for EVERY unscheduled pseudo-stop: picking
  // a second unscheduled stop toggled the first one off.
  const selectedMultipleSteps = (step, driverId) => {
    const cloneSteps = [...multipleStepState.steps];
    const indexStep = cloneSteps.findIndex((s) => isSameSelectedStep(s, step, driverId));
    if (indexStep > -1) {
      cloneSteps.splice(indexStep, 1);
    } else {
      cloneSteps.unshift({ ...step, driverId });
    }
    setMultipleStepState({ ...multipleStepState, steps: cloneSteps });
  };

  // ─── Multi-select: rubber-band + bulk actions (Diego, Aug 18 2026) ────────
  // Shift/Ctrl+click toggles single stops (pre-existing); NEW: dragging a
  // rectangle on empty timeline space selects every live badge it touches,
  // and a floating bar offers "Unassign all" on the selection. Esc clears.
  const [rubberRect, setRubberRect] = useState(null);

  const clearMultiSelection = () => setMultipleStepState(initMultipleSteps);

  const selectedScheduledSteps = (multipleStepState.steps || []).filter((s) => s.driverId !== 'unscheduled');
  const selectedOrderCodes = uniq(
    selectedScheduledSteps.flatMap((s) =>
      (s.orderIds || (s.orderId ? [s.orderId] : []))
        .map((oid) => orders.find((o) => o.id === oid)?.orderCode)
        .filter(Boolean),
    ),
  );

  const handleBulkUnassign = async () => {
    if (!selectedOrderCodes.length) return;
    await handleUnassignStop(selectedOrderCodes);
    clearMultiSelection();
  };

  // "Send to driver" (Diego, Aug 18 2026) — semantics equivalent to:
  // unassign the whole selection, drag it onto the target driver's timeline
  // (appended at the end), click Apply. Two server steps for a reason: a
  // reorder may not REMOVE stops from a started route (OVO-254 — that path
  // 400s), while change-to-unassigned handles started routes fine; the
  // follow-up reorder then only ADDS route-less orders, which is allowed on
  // every route state.
  const [sendToDriverOpen, setSendToDriverOpen] = useState(false);
  const [sendTargetRouteId, setSendTargetRouteId] = useState(null);
  const [sendingToDriver, setSendingToDriver] = useState(false);

  const sendCandidateRoutes = (routes || []).filter((r) => r.actionStatus !== ACTION_ROUTE.COMPLETED);

  // Same leg-key construction as handleReorder (normalized SERVICE →
  // `_DELIVERY`, done legs omitted — the backend re-injects them).
  const buildLegKeysForDriver = (driverKey) => {
    const legs = [];
    (solutions[driverKey] || []).forEach((step) => {
      if (step.type === STEP_TYPE.driver) return;
      const legType = step.type.toUpperCase() === 'SERVICE' ? 'DELIVERY' : step.type.toUpperCase();
      (step.orderIds || (step.orderId ? [step.orderId] : [])).forEach((oid) => {
        const order = orders.find((o) => o.id === oid);
        if (!order || order.status === 'SKIPPED') return;
        if (legType === 'PICKUP' && order.pickupStatus === 'COLLECTED') return;
        if (legType === 'DELIVERY' && order.status === 'COMPLETED') return;
        const key = `${order.id}_${legType}`;
        if (!legs.includes(key)) legs.push(key);
      });
    });
    return legs;
  };

  // `targetRouteId` defaults to the modal's pick; a drag of a selected stop
  // onto another route passes the drop target (#129).
  const handleBulkSendToDriver = async (targetRouteId = sendTargetRouteId) => {
    const targetRoute = routes.find((r) => r.id === targetRouteId);
    if (!targetRoute) return;
    const targetDriverKey = targetRoute.driver?.id;

    // Selected orders, each with the route it currently sits on (undefined
    // for unscheduled selections — those just get assigned).
    const movedById = new Map();
    (multipleStepState.steps || []).forEach((s) => {
      const sourceRoute = s.driverId === 'unscheduled' ? null : routes.find((r) => r.driver?.id === s.driverId);
      (s.orderIds || (s.orderId ? [s.orderId] : [])).forEach((oid) => {
        const order = orders.find((o) => o.id === oid);
        if (!order) return;
        // Already on the target → nothing to move for this order.
        if (sourceRoute && sourceRoute.id === targetRoute.id) return;
        movedById.set(order.id, { order, sourceRouteId: sourceRoute?.id ?? null });
      });
    });
    const moved = [...movedById.values()];
    if (!moved.length) {
      setSendToDriverOpen(false);
      return;
    }

    setSendingToDriver(true);
    let followingRun = false;
    try {
      // 1. Unassign everything that sits on another route.
      const toUnassignCodes = uniq(moved.filter((m) => m.sourceRouteId != null).map((m) => m.order.orderCode));
      if (toUnassignCodes.length) {
        const res = await dispatch(doChangeToUnassigned({ orderCodes: toUnassignCodes }));
        if (doChangeToUnassigned.rejected.match(res)) {
          api.open({
            type: 'error',
            content: res.payload?.message || t('stopClicked.unassignFailed'),
            style: { marginTop: '52px' },
          });
          return;
        }
      }

      // 2. Add the moved orders to the target driver's sequence and apply —
      // into its drop-off block when they go where it already delivers (#131).
      const targetSteps = insertMovedLegs(
        buildLegKeysForDriver(targetDriverKey),
        moved.map(({ order }) => order),
        (id) => orders.find((o) => o.id === id),
      );
      const resReorder = await dispatch(
        doReorderOrdersForRoutes({
          date: date?.format(DATE_BACKEND_FORMAT),
          routes: [{ driverId: targetRoute.id, steps: targetSteps }],
          unscheduled: [],
          allOrderIds: uniq(targetSteps.map((k) => Number(k.split('_')[0]))),
        }),
      );
      if (doReorderOrdersForRoutes.rejected.match(resReorder)) {
        api.open({
          type: 'error',
          content: resReorder.payload?.message || t('reorderFailed'),
          style: { marginTop: '52px' },
        });
        // The unassign already went through — refetch so the operator sees
        // the orders waiting in the Unscheduled pool, nothing is lost.
        return;
      }
      api.open({
        type: 'success',
        content: t('multiSelect.sendDone', { count: moved.length, driver: targetRoute.driver?.name }),
        style: { marginTop: '52px' },
      });
      clearMultiSelection();
      setSendToDriverOpen(false);
      setSendTargetRouteId(null);
      // #125 — the target route is rewritten by the optimizer callback: wait
      // for it instead of reloading a plan that isn't applied yet.
      setIsOptimize(true);
      followingRun = followRun(resReorder.payload);
      if (!followingRun) setIsOptimize(false);
    } finally {
      setSendingToDriver(false);
      if (!followingRun) await getAllDataForPage();
    }
  };

  const onTimelineMouseDown = (e) => {
    if (e.button !== 0 || isDragging) return;
    // Rubber only starts on EMPTY timeline space — never hijack badges (they
    // are dnd drag handles), buttons, links or open popovers.
    if (e.target.closest('.unit, button, a, input, .ant-popover')) return;
    const start = { x: e.clientX, y: e.clientY, additive: e.shiftKey || e.ctrlKey || e.metaKey };
    e.preventDefault();
    const onMove = (ev) => {
      setRubberRect({
        left: Math.min(start.x, ev.clientX),
        top: Math.min(start.y, ev.clientY),
        width: Math.abs(ev.clientX - start.x),
        height: Math.abs(ev.clientY - start.y),
      });
    };
    const onUp = (ev) => {
      window.removeEventListener('mousemove', onMove);
      setRubberRect(null);
      const rect = {
        left: Math.min(start.x, ev.clientX),
        top: Math.min(start.y, ev.clientY),
        right: Math.max(start.x, ev.clientX),
        bottom: Math.max(start.y, ev.clientY),
      };
      // A tiny drag is a click, not a selection.
      if (rect.right - rect.left < 5 && rect.bottom - rect.top < 5) return;
      const hits = [];
      (containerTimelineRef.current || document).querySelectorAll('[data-ms-step]').forEach((el) => {
        const b = el.getBoundingClientRect();
        const overlaps = b.left < rect.right && b.right > rect.left && b.top < rect.bottom && b.bottom > rect.top;
        if (!overlaps) return;
        const [driverKey, stepId, type] = el.dataset.msStep.split('|');
        const step = (solutions[driverKey] || []).find((s) => String(s.stepId) === stepId && s.type === type);
        if (step) hits.push({ ...step, driverId: Number(driverKey) });
      });
      setMultipleStepState((prev) => {
        const base = start.additive ? [...prev.steps] : [];
        hits.forEach((hit) => {
          if (!base.some((s) => isSameSelectedStep(s, hit, hit.driverId))) {
            base.push(hit);
          }
        });
        return { ...prev, steps: base };
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp, { once: true });
  };

  const onClickStep = (step) => {
    // click close step
    if (!step) {
      setSelectedStep({});
      return;
    }

    if (selectedStep.id === step.id && selectedStep.type === step.type) {
      setSelectedStep({});
    } else {
      setSelectedStep({ id: step.id, type: step.type });
    }
  };

  const hours = range(hourStart, hourEnd + 2);
  const minutes = range(0, (hourEnd - hourStart) * 2 + 2);

  useEffect(() => {
    if (routes?.length) {
      // Fit the time axis to the VISIBLE routes only. When the dispatcher
      // isolates a driver (hides the rest via the Shifts panel), the axis
      // tightens to that route's shift span instead of spanning every driver's
      // 06:00–17:00 envelope — so all their stops fit without scrolling right.
      // With everyone visible this is identical to the previous behaviour.
      const visibleRoutes = routes.filter((r) => !hideDriverIds?.includes(r.driver.id));
      const rangeRoutes = visibleRoutes.length ? visibleRoutes : routes;
      const [startAt, endAt] = getFirstStartAndLastEnd(rangeRoutes);
      const startHour = startAt && startAt?.split(':')[0];
      setHourStart(Number(startHour));
      const endHour = endAt && endAt?.split(':')[0];
      setHourEnd(Number(endHour));
      const startHourFull = `${startHour}:00`;
      setStartTime(startHourFull);
      if (stateTimeLine.maxTimePx || stateTimeLine.minTimePx) {
        if (stateTimeLine.minTimeMinutes) {
          const pxScroll = convertMinutesToPixel(stateTimeLine.minTimeMinutes, oneHourPx);
          containerTimelineRef.current.scrollTo({
            left: pxScroll - 80,
          });
        }
        setStateTimeLine(initStateTimeline);
      }
    }
  }, [routes, oneHourPx, date, hideDriverIds]);

  // When the visible driver set changes (isolate / reveal), the axis re-fits to
  // a new span — reset the horizontal scroll so it starts at the left edge
  // rather than keeping a stale offset. Kept separate from the range effect so
  // zooming (oneHourPx) doesn't yank the scroll back to the start.
  useEffect(() => {
    if (containerTimelineRef.current) {
      containerTimelineRef.current.scrollTo({ left: 0 });
    }
  }, [hideDriverIds]);
  const onDragStart = () => {
    setIsDragging(true);
  };

  const isHasRouteStarted = useMemo(() => {
    return routes?.some((route) => route.steps?.some((step) => step.status === 'COMPLETED'));
  }, [routes]);

  const onDragEnd = (result) => {
    setIsDragging(false);
    const { source, destination } = result;
    if (!destination) return;
    if (source.droppableId === 'unscheduled' && destination.droppableId === 'unscheduled') return;

    // Hard-block drops onto a route the driver has already ended
    // (actionStatus = COMPLETED). The dispatcher can release the order
    // anywhere, but the state never changes and a red toast explains
    // why. Covers both "unscheduled → completed route" and
    // "route A → completed route B". Source-side same-route reorders
    // are not blocked here because by then the route is finished and
    // its steps are already drag-disabled per-step in Steps.jsx.
    if (destination.droppableId !== 'unscheduled' && source.droppableId !== destination.droppableId) {
      const destRoute = routes?.find((r) => String(r?.driver?.id) === String(destination.droppableId));
      if (destRoute?.actionStatus === ACTION_ROUTE.COMPLETED) {
        api.open({
          type: 'error',
          content: t('cannotAddToCompletedRoute'),
          style: { marginTop: '52px' },
        });
        return;
      }
    }

    // #129 — dragging ONE stop of a multi-selection onto another route used to
    // move only that stop (the selection was ignored here). Move the whole
    // selection instead: same server flow as "Send to driver…", or unassign
    // everything when it's dropped on the Unscheduled pool.
    const selection = multipleStepState.steps || [];
    const draggedStep = solutions[source.droppableId]?.[source.index];
    if (
      selection.length > 1 &&
      source.droppableId !== destination.droppableId &&
      draggedStep &&
      selection.some((s) => isSameSelectedStep(s, draggedStep, source.droppableId))
    ) {
      if (destination.droppableId === 'unscheduled') {
        handleBulkUnassign();
        return;
      }
      const destRoute = routes?.find((r) => String(r?.driver?.id) === String(destination.droppableId));
      if (destRoute) {
        handleBulkSendToDriver(destRoute.id);
        return;
      }
    }

    const newDragDriverIds = [
      ...new Set([...dragDriverIds, source.droppableId, destination.droppableId].filter((id) => id !== 'unscheduled')),
    ];

    setDragDriverIds(newDragDriverIds);

    if (source.droppableId === destination.droppableId) {
      if (destination.index === source.index) return;
      if (destination.droppableId?.includes('assign') || destination.droppableId === 'unscheduled') return;

      const steps = solutions[source.droppableId];
      const sourceClone = [...steps];
      const stop = sourceClone[source.index];
      const type = stop.type === STEP_TYPE.pickup ? STEP_TYPE.delivery : STEP_TYPE.pickup;
      const stopOrderIds = stop.orderIds || [];

      if (stop.type === STEP_TYPE.delivery) {
        // For delivery, check if any orderId in stop.orderIds has a pickup before destination
        let isFalse = false;
        forEach(stopOrderIds, (orderId) => {
          if (isFalse) return;
          const stopPickupIndex = findIndex(sourceClone, (s) => s.orderIds?.includes(orderId) && s.type === type);
          if (stopPickupIndex > -1) {
            isFalse = destination.index <= stopPickupIndex;
          }
        });
        if (isFalse) {
          api.open({
            type: 'error',
            content: t('dropoffBeforePickup'),
            style: { marginTop: '52px' },
          });
          return;
        }
      } else {
        // For pickup, check if any orderId in stop.orderIds has a delivery after destination
        let isFalse = false;
        forEach(stopOrderIds, (orderId) => {
          if (isFalse) return;
          const stopDeliveryIndex = findIndex(sourceClone, (s) => s.orderIds?.includes(orderId) && s.type === type);
          if (stopDeliveryIndex > -1) {
            isFalse = destination.index >= stopDeliveryIndex;
          }
        });
        if (isFalse) {
          api.open({
            type: 'error',
            content: t('dropoffBeforePickup'),
            style: { marginTop: '52px' },
          });
          return;
        }
      }

      // Simply move the single step without moving related steps
      const [removed] = sourceClone.splice(source.index, 1);
      sourceClone.splice(destination.index, 0, {
        ...removed,
        duration: 20,
        marginLeft: 8,
      });

      const newSolutions = { ...solutions, [source.droppableId]: sourceClone };
      commitSolutions(newSolutions);
    } else {
      moveStep(source, destination);
    }
  };

  const moveStep = (droppableSource, droppableDestination) => {
    const stepsSource = solutions[droppableSource.droppableId];
    const stepsDestination = solutions[droppableDestination.droppableId];
    const sourceClone = [...stepsSource];
    const destClone = [...stepsDestination];
    const stop = sourceClone[droppableSource.index];
    const type = stop.type === STEP_TYPE.pickup ? STEP_TYPE.delivery : STEP_TYPE.pickup;
    const newSolutions = { ...solutions };

    if (stop.type === STEP_TYPE.pickup) {
      const orderIds = stop.orderIds || [];

      // Remove pickup from source route
      const [removedPickup] = sourceClone.splice(droppableSource.index, 1);

      // Split related delivery steps in source route:
      // - remove these orderIds from original delivery's orderIds
      // - create new delivery step(s) that will move with this pickup
      const deliveryInfos = [];
      sourceClone.forEach((s, idx) => {
        if (s.type === type && s.orderIds?.some((orderId) => orderIds.includes(orderId))) {
          const intersectionOrderIds = s.orderIds.filter((orderId) => orderIds.includes(orderId));
          const remainingOrderIds = s.orderIds.filter((orderId) => !orderIds.includes(orderId));
          deliveryInfos.push({ index: idx, step: s, intersectionOrderIds, remainingOrderIds });
        }
      });

      // New delivery steps that move with this pickup (one per affected delivery step)
      const newDeliverySteps = deliveryInfos
        .filter((info) => info.intersectionOrderIds.length > 0)
        .map((info) => ({
          ...info.step,
          id: uuidv4(),
          orderIds: info.intersectionOrderIds,
          marginLeft: 8,
        }));

      // Update/remove original delivery steps in source route
      const sortedInfos = [...deliveryInfos].sort((a, b) => b.index - a.index);
      sortedInfos.forEach((info) => {
        if (info.remainingOrderIds.length === 0) {
          // No orderIds left -> remove delivery step
          sourceClone.splice(info.index, 1);
        } else {
          // Keep delivery step with remaining orderIds
          sourceClone.splice(info.index, 1, {
            ...info.step,
            orderIds: info.remainingOrderIds,
          });
        }
      });

      // Insert / merge pickup into destination route
      const destPickupIndex = destClone.findIndex(
        (dest) =>
          dest.lat === removedPickup.lat &&
          dest.lng === removedPickup.lng &&
          dest.locationId === removedPickup.locationId,
      );
      const destPickup = destPickupIndex === -1 ? null : destClone[destPickupIndex];
      let pickupIndexInDest;

      if (destPickup) {
        destClone.splice(destPickupIndex, 1, {
          ...destPickup,
          // duration: 10,
          orderIds: [...destPickup.orderIds, ...orderIds],
        });
        pickupIndexInDest = destPickupIndex;
      } else {
        destClone.splice(droppableDestination.index, 0, {
          ...removedPickup,
          id: uuidv4(),
          orderIds: [...orderIds],
          marginLeft: 8,
          // width: '20px',
        });
        pickupIndexInDest = droppableDestination.index;
      }

      // Insert new delivery step(s) right after the pickup in destination route
      newDeliverySteps.forEach((deliveryStep, idx) => {
        destClone.splice(pickupIndexInDest + 1 + idx, 0, {
          ...deliveryStep,
          marginLeft: 8,
        });
      });
    } else {
      // Delivery step
      const orderIds = stop.orderIds || [];
      const [removed] = sourceClone.splice(droppableSource.index, 1);
      destClone.splice(droppableDestination.index, 0, {
        ...removed,
        id: `${removed.id}-${removed.type}`,
        marginLeft: 8,
      });

      // Find pickup steps for all orderIds in this delivery
      orderIds.forEach((orderId) => {
        const stopPickupIndex = findIndex(sourceClone, (s) => s.orderIds?.includes(orderId) && s.type === type);
        if (stopPickupIndex > -1) {
          const stopPickup = sourceClone[stopPickupIndex];
          const pickupOrderIds = stopPickup.orderIds || [];
          if (pickupOrderIds.length === 1) {
            const destinationIndex = droppableDestination.index;
            const [removedLeft] = sourceClone.splice(stopPickupIndex, 1);
            destClone.splice(destinationIndex, 0, {
              ...removedLeft,
              id: `${removedLeft.id}-${removedLeft.type}`,
              marginLeft: 8,
              orderIds: [orderId],
            });
          } else if (pickupOrderIds.length > 1) {
            const destinationIndex =
              type === STEP_TYPE.pickup ? droppableDestination.index : droppableDestination.index + 1;
            const orderIdIndex = findIndex(pickupOrderIds, (id) => id === orderId);
            pickupOrderIds.splice(orderIdIndex, 1);
            sourceClone.splice(stopPickupIndex, 1, {
              ...stopPickup,
              orderIds: pickupOrderIds,
              id: uuidv4(),
              marginLeft: 8,
            });
            destClone.splice(destinationIndex, 0, {
              ...stopPickup,
              orderIds: [orderId],
              marginLeft: 8,
              id: uuidv4(),
            });
          }
        }
      });
    }
    newSolutions[droppableSource.droppableId] = sourceClone;
    newSolutions[droppableDestination.droppableId] = destClone;
    commitSolutions(newSolutions);
  };

  const handleCancelMultiStops = () => {
    setMultipleStepState(initMultipleSteps);
  };

  const onSelectChangeAddOrders = (newSelectedRowKeys) => {
    setOrdersSelected(newSelectedRowKeys);
  };

  const changeOrdersToUnassigned = async () => {
    await dispatch(
      doChangeToUnassigned({
        orderCodes: ordersSelected,
      }),
    );

    await getAllDataForPage();
  };

  const changeOrdersToDraft = async () => {
    await dispatch(
      doChangeToDraft({
        orderCodes: ordersSelected,
      }),
    );
    await getAllDataForPage();
  };

  // OVO-153 — unassign a (possibly merged) stop straight from the stop
  // popup. Works on STARTED routes for stops the driver hasn't validated
  // yet: no re-optimization, the driver simply sees one fewer step on the
  // next poll, the order(s) go back to Unscheduled. The backend guard
  // rejects stops already started/completed by the driver.
  const handleUnassignStop = async (orderCodes) => {
    const res = await dispatch(doChangeToUnassigned({ orderCodes }));
    if (doChangeToUnassigned.rejected.match(res)) {
      api.open({
        type: 'error',
        content: res.payload?.message || t('stopClicked.unassignFailed'),
        style: { marginTop: '52px' },
      });
      return;
    }
    api.open({
      type: 'success',
      content: t('stopClicked.unassignDone', { count: orderCodes.length }),
      style: { marginTop: '52px' },
    });
    setSelectedStep({});
    await getAllDataForPage();
  };

  // "Cancel stop" (Seb) — same mechanics as unassign (guards, refresh), but
  // the order lands on the terminal CANCELLED status instead of returning to
  // the unscheduled pool.
  const handleCancelStop = async (orderCodes) => {
    const res = await dispatch(doChangeToCancelled({ orderCodes }));
    if (doChangeToCancelled.rejected.match(res)) {
      api.open({
        type: 'error',
        content: res.payload?.message || t('stopClicked.cancelFailed'),
        style: { marginTop: '52px' },
      });
      return;
    }
    api.open({
      type: 'success',
      content: t('stopClicked.cancelDone', { count: orderCodes.length }),
      style: { marginTop: '52px' },
    });
    setSelectedStep({});
    await getAllDataForPage();
  };

  const handleKeyUp = (event) => {
    if (event.key === 'Escape') {
      clearMultiSelection();
      return;
    }
    if (event.key === 'Meta' || event.key === 'Control' || event.key === 'Shift') {
      // if (multipleStepState.steps?.length > 0) {
      //   setMultipleStepState({
      //     ...multipleStepState,
      //     visibleModal: true,
      //   });
      // }
    }
  };

  useEffect(() => {
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [multipleStepState, stateTimeLine]);

  const formatSolutions = () => {
    console.log('[formatSolutions] Starting with routes:', routes?.length, routes);
    const solutionsTmp = {};
    const mapDataTmp = {};
    // Hard date gate (OVO-005 class, Aug 26 2026 incident): the orders slice
    // is shared with other views (the Orders page fetches it UNFILTERED, and
    // quick date switches leave overlapping responses racing), so never trust
    // it to be scoped — an order renders on this plan only if its
    // deliveryDate IS the plan's date. Same-day-different-identity dayjs is
    // fine: we compare formatted strings.
    const planDateStr = date?.format(DATE_BACKEND_FORMAT);
    const sameDay = (d) => !planDateStr || (d && String(d).slice(0, 10) === planDateStr);
    const unassignOrders = orders.filter((o) => o.status === 'UNASSIGNED' && sameDay(o.deliveryDate));
    const allStopsTmp = [];

    const stepsUnassigned = [];
    let indexUnassigned = 1;
    for (let index = 0; index < unassignOrders.length; index++) {
      const order = unassignOrders[index];
      const ticketNames = order?.tickets?.map((t) => orderTickets?.find((ot) => ot.id === t)?.name) || [];
      if (order) {
        const paramsStepPickup = {
          id: `${order.orderCode}-pickup`,
          stepId: null,
          orderCode: order.orderCode,
          name: order.pickupName,
          location: order.pickupName,
          address: order.pickupAddress,
          lat: order.pickupAddressLat,
          lng: order.pickupAddressLng,
          startTime: order?.pickupTimeStart,
          partner: order?.partner,
          volume: order?.volume,
          type: STEP_TYPE.pickup,
          endTime: order?.pickupTimeEnd,
          status: order.status,
          index: indexUnassigned,
          orderNumber: indexUnassigned,
          duration: order.pickupDuration,
          orderId: order.id,
          orderIds: [order.id],
          orderCode: order.orderCode,
          deliveryName: order.deliveryName,
          orderTypes: order.types,
          partner: order?.partner,
          driverName: 'Unscheduled',
          volume: order?.volume,
          color: '#606060',
          routeId: 'unscheduled',
          unscheduledReason: order.unscheduledReason,
          ticketNames: ticketNames || [],
          // OVO-149/150 — searchable ids + real partner identity (the bare
          // `partner` above is the legacy free-text column).
          orderRef: order.orderRef,
          partnerOrderRef: order.partnerOrderRef,
          partnerName: order.partnerRef?.name,
          partnerCode: order.partnerRef?.code,
          partnerId: order.partnerRef?.id ?? order.partnerId ?? null,
          // OVO-143 — both legs' windows on every step so the popup can show
          // the constraint (e.g. delivery by 8:30) regardless of which leg.
          pickupTimeStart: order.pickupTimeStart,
          pickupTimeEnd: order.pickupTimeEnd,
          deliveryTimeStart: order.deliveryTimeStart,
          deliveryTimeEnd: order.deliveryTimeEnd,
        };
        const paramsStepDelivery = {
          id: `${order.orderCode}-delivery`,
          stepId: null,
          orderCode: order.orderCode,
          name: order.deliveryName,
          location: order.deliveryName,
          address: order.deliveryAddress,
          lat: order.deliveryAddressLat,
          lng: order.deliveryAddressLng,
          startTime: order?.deliveryTimeStart,
          type: STEP_TYPE.delivery,
          endTime: order?.deliveryTimeEnd,
          status: order.status,
          index: indexUnassigned + 1,
          orderNumber: indexUnassigned + 1,
          duration: order.deliveryDuration,
          orderId: order.id,
          orderIds: [order.id],
          orderCode: order.orderCode,
          deliveryName: order.deliveryName,
          orderTypes: order.types,
          partner: order?.partner,
          volume: order?.volume,
          driverName: 'Unscheduled',
          color: '#606060',
          routeId: 'unscheduled',
          ticketNames: ticketNames || [],
          unscheduledReason: order.unscheduledReason,
          // OVO-149/150 — searchable ids + real partner identity.
          orderRef: order.orderRef,
          partnerOrderRef: order.partnerOrderRef,
          partnerName: order.partnerRef?.name,
          partnerCode: order.partnerRef?.code,
          partnerId: order.partnerRef?.id ?? order.partnerId ?? null,
          // OVO-143 — both legs' windows (see pickup step above).
          pickupTimeStart: order.pickupTimeStart,
          pickupTimeEnd: order.pickupTimeEnd,
          deliveryTimeStart: order.deliveryTimeStart,
          deliveryTimeEnd: order.deliveryTimeEnd,
        };
        if (order.orderType === 'SERVICE') {
          // SERVICE = one visit. Rendering the usual pickup+delivery pair
          // produced two "phantom" unscheduled twins per service order
          // (dev report, Aug 18 2026) — and dragging those fake legs fed the
          // reorder bogus `_PICKUP` keys. One pseudo-step, service-typed;
          // the delivery-leg identity mirrors the service location.
          const paramsStepService = {
            ...paramsStepDelivery,
            id: `${order.orderCode}-service`,
            type: STEP_TYPE.service,
            index: indexUnassigned,
            orderNumber: indexUnassigned,
          };
          stepsUnassigned.push(paramsStepService);
          allStopsTmp.push(paramsStepService);
          indexUnassigned += 1;
        } else {
          stepsUnassigned.push(paramsStepPickup);
          stepsUnassigned.push(paramsStepDelivery);
          allStopsTmp.push(paramsStepPickup);
          allStopsTmp.push(paramsStepDelivery);
          indexUnassigned += 2;
        }
      }
    }
    const rsUnassignedStops = {
      steps: stepsUnassigned,
      color: '#606060',
    };

    solutionsTmp['unscheduled'] = stepsUnassigned;

    mapDataTmp['unscheduled'] = rsUnassignedStops;

    routes.forEach((route) => {
      // Same hard date gate as the unscheduled list: a stale routes response
      // from a previously-viewed day must not render on this plan.
      if (route.date && !sameDay(route.date)) return;
      const driver = route.driver;
      const color = '#' + COLORS[driver.id % COLORS.length];

      // Build the positioned + merged badge list for ONE time-source. Called
      // twice per route: with the actual (log-derived) times — the canonical
      // list feeding the map + drag-drop + bottom row — and with the planned
      // optimizer ETAs for the clean reference row. Only the actual pass
      // contributes to the global stop/map aggregates (`pushGlobals`).
      const buildNewSteps = (mappedSteps, { pushGlobals }) => {
        const steps = calculatePixelBetweenStops(mappedSteps, route, oneHourPx, { planned: !pushGlobals });
        const newSteps = [];
        let indexStep = 1;

        if (steps.length > 0) {
          for (let index = 0; index < steps.length; index++) {
            const step = steps[index];
            if (!step) {
              console.log('[formatSolutions] WARNING: step', index, 'is null/undefined, skipping');
              continue; // Changed from return to continue
            }

            const width = step.arrivalTime ? getWidthChart(step.arrivalTime, step.finishTime, oneHourPx) : 1;

            const orderData = orders.find((o) => o.id === step.orderId);
            const ticketNames = orderData?.tickets?.map((t) => orderTickets?.find((ot) => ot.id === t)?.name) || [];

            const stepResponse = {
              id: `${step.id}-${step.type}`,
              stepId: step.id,
              orderNumber: step.orderNumber,
              driverName: driver?.name,
              name: step.type === STEP_TYPE.pickup ? step.order?.pickupName : step.order?.deliveryName,
              location: step.order?.pickupName,
              address: step.order?.pickupAddress,
              lat: step.order?.pickupAddressLat,
              lng: step.order?.pickupAddressLng,
              arrivalTime: step.arrivalTime,
              finishTime: step.finishTime,
              // Planned (optimizer ETA) times, kept alongside the actual/blended
              // arrivalTime/finishTime above so the stop popup can show both.
              plannedArrivalTime: step.originalArrivalTime,
              plannedFinishTime: step.originalFinishTime,
              startTime: step.order?.deliveryTimeStart,
              endTime: step.order?.deliveryTimeEnd,
              status: step.status,
              type: step.type,
              distance: step.distance,
              orderNumber: step.orderNumber,
              index: indexStep,
              duration: step.type === STEP_TYPE.pickup ? step.order?.pickupDuration : step.order?.deliveryDuration,
              marginLeft: step.marginLeft,
              width,
              orderIds: [step.orderId],
              orderCode: step.order?.orderCode,
              deliveryName: step.order?.deliveryName,
              orderRef: step.order?.orderRef,
              // OVO-149/150 — searchable partner ref + real partner identity
              // (the `partner` field below is the legacy free-text column).
              partnerOrderRef: step.order?.partnerOrderRef,
              partnerName: step.order?.partnerRef?.name,
              partnerCode: step.order?.partnerRef?.code,
              partnerId: step.order?.partnerRef?.id ?? step.order?.partnerId ?? null,
              orderStatus: step.order?.status,
              pickupStatus:
                step.order?.status === ORDER_STATUSES.SKIPPED ? ORDER_STATUSES.SKIPPED : step.order?.pickupStatus,
              driverName: driver?.name,
              travelMins: step.travelMins || 0,
              workingMins: step.workingMins || 0,
              waitingMins: step.waitingMins || 0,
              color,
              orderTypes: step.order?.types,
              partner: orderData?.partner,
              volume: orderData?.volume,
              logs: step.order?.logs || [],
              order: step.order,
              orderType: step.order?.orderType,
              ticketNames: ticketNames || [],
              collectedItems: step.collectedItems || [],
            };

            if (step.type === STEP_TYPE.delivery) {
              stepResponse.location = step.order?.deliveryName;
              stepResponse.address = step.order?.deliveryAddress;
              stepResponse.lat = step.order?.deliveryAddressLat;
              stepResponse.lng = step.order?.deliveryAddressLng;

              let isCompletedDelivery = step.status === STEP_STATUS.COMPLETED;
              let sameDelivery = false;
              const orderIds = [...stepResponse.orderIds];

              if (index < steps.length - 1) {
                do {
                  const currentNext = steps[index];
                  const stepNext = steps[index + 1];
                  if (!isCompletedDelivery && stepNext?.type === STEP_TYPE.delivery && stepNext?.order)
                    isCompletedDelivery = stepNext.status === STEP_STATUS.COMPLETED;
                  const widthNextStep = stepNext?.arrivalTime
                    ? getWidthChart(stepNext.arrivalTime, stepNext.finishTime, oneHourPx)
                    : 1;
                  sameDelivery =
                    stepNext &&
                    stepNext.type === STEP_TYPE.delivery &&
                    currentNext.status === stepNext.status &&
                    currentNext.order?.deliveryAddressLat === stepNext.order?.deliveryAddressLat &&
                    currentNext.order?.deliveryAddressLng === stepNext.order?.deliveryAddressLng &&
                    currentNext.order?.deliveryPhone === stepNext.order?.deliveryPhone &&
                    currentNext.order?.deliveryName === stepNext.order?.deliveryName;

                  if (sameDelivery) {
                    stepResponse.duration += stepNext.duration;
                    stepResponse.distance += stepNext.distance;
                    stepResponse.travelMins += stepNext.travelMins;
                    stepResponse.workingMins += stepNext.workingMins;
                    stepResponse.waitingMins = stepNext.waitingMins;
                    stepResponse.finishTime = stepNext.finishTime;
                    stepResponse.plannedFinishTime = stepNext.originalFinishTime;
                    stepResponse.width += widthNextStep;
                    stepResponse.marginLeft += stepNext.marginLeft;
                    orderIds.push(stepNext.orderId);
                    index++;
                  }
                } while (sameDelivery);
              }
              stepResponse.status = isCompletedDelivery ? 'COMPLETED' : 'IN_PROGRESS';
              stepResponse.id = `${orderIds.length}||${stepResponse.id}-${orderIds.length}`;
              stepResponse.orderIds = orderIds;
              // The merge above SUMMED each merged order's individual
              // (arrival→finish) width. When several orders share one physical
              // stop, their spans OVERLAP, so summing balloons the badge (e.g. 3
              // orders each batch-marked ~70min after arrival → a 3×70min block).
              // The badge represents ONE stop, so its width is the single merged
              // span (first arrival → last finish), computed once.
              stepResponse.width = stepResponse.arrivalTime
                ? getWidthChart(stepResponse.arrivalTime, stepResponse.finishTime, oneHourPx)
                : 1;
            } else if (step.type === STEP_TYPE.pickup) {
              let isCompletedPickup =
                stepResponse.orderStatus === ORDER_STATUSES.SKIPPED ||
                stepResponse.pickupStatus === ORDER_STATUSES.COLLECTED ||
                stepResponse.pickupStatus === ORDER_STATUSES.PICKUP_FAILED;

              delete stepResponse.orderStatus; // No need
              delete stepResponse.pickupStatus; // No need
              let partner = stepResponse?.order?.partner || '';
              let samePickup = false;
              const orderIds = [...stepResponse.orderIds];
              if (index < steps.length - 1) {
                do {
                  const currentNext = steps[index];
                  const stepNext = steps[index + 1];
                  if (!isCompletedPickup && stepNext?.type === STEP_TYPE.pickup && stepNext?.order)
                    isCompletedPickup =
                      stepNext.order.status === ORDER_STATUSES.SKIPPED ||
                      stepNext.order.pickupStatus === ORDER_STATUSES.COLLECTED ||
                      stepNext.order.pickupStatus === ORDER_STATUSES.PICKUP_FAILED;
                  const widthNextStep = stepNext?.arrivalTime
                    ? getWidthChart(stepNext.arrivalTime, stepNext.finishTime, oneHourPx)
                    : 1;
                  // OVO-023 — disambiguate by pickupName, not pickupPhone:
                  // the same depot arrives with a phone on some orders and
                  // an empty one on others, which fragmented one physical
                  // pickup into several timeline badges ("8 pickups").
                  samePickup =
                    stepNext &&
                    stepNext.type === STEP_TYPE.pickup &&
                    currentNext.status === stepNext.status &&
                    currentNext.order?.pickupAddressLat === stepNext.order?.pickupAddressLat &&
                    currentNext.order?.pickupAddressLng === stepNext.order?.pickupAddressLng &&
                    currentNext.order?.pickupName === stepNext.order?.pickupName;

                  if (samePickup) {
                    if (stepNext.order?.partner) {
                      const partnerList = partner
                        .split(',')
                        .map((p) => p.trim())
                        .filter(Boolean);

                      if (!partnerList.includes(stepNext.order?.partner)) {
                        partnerList.push(stepNext.order?.partner);
                        partner = partnerList.join(', ');
                      }
                    }
                    stepResponse.duration += stepNext.duration;
                    stepResponse.distance += stepNext.distance;
                    stepResponse.travelMins += stepNext.travelMins;
                    stepResponse.workingMins += stepNext.workingMins;
                    stepResponse.waitingMins = stepNext.waitingMins;
                    stepResponse.finishTime = stepNext.finishTime;
                    stepResponse.plannedFinishTime = stepNext.originalFinishTime;
                    stepResponse.width += widthNextStep;
                    stepResponse.marginLeft += stepNext.marginLeft;
                    orderIds.push(stepNext.orderId);
                    index++;
                  }
                } while (samePickup);
              }
              stepResponse.status = isCompletedPickup ? 'COMPLETED' : 'IN_PROGRESS';
              stepResponse.id = `${orderIds.length}||${stepResponse.id}-${orderIds.length}`;
              stepResponse.orderIds = orderIds;
              stepResponse.partner = partner;
              // See the delivery branch: the merged badge is ONE physical stop, so
              // its width is the single span (first arrival → last finish), not the
              // sum of each merged order's own — overlapping — span.
              stepResponse.width = stepResponse.arrivalTime
                ? getWidthChart(stepResponse.arrivalTime, stepResponse.finishTime, oneHourPx)
                : 1;
            }
            newSteps.push(stepResponse);
            if (pushGlobals) allStopsTmp.push(stepResponse);
            indexStep += 1;
          }
        }
        return newSteps;
      };

      // Canonical actual/blended steps — drive the map, drag-drop, bottom row.
      const newSteps = buildNewSteps(
        route?.steps?.map((step) => ({
          ...step,
          arrivalTime: getArrivalTime(step),
          finishTime: getFinishTime(step),
        })) || [],
        { pushGlobals: true },
      );
      // Planned reference steps (raw optimizer ETAs — always monotonic/clean).
      // Rendered as a second stacked row for started routes (see DriverList).
      const plannedSteps = buildNewSteps(
        route?.steps?.map((step) => ({
          ...step,
          arrivalTime: step.arrivalTime,
          finishTime: step.finishTime,
        })) || [],
        { pushGlobals: false },
      );

      solutionsTmp[route.driver.id] = newSteps;
      solutionsTmp[`planned_${route.driver.id}`] = plannedSteps;
      solutionsTmp[`assign_${route.driver.id}`] = [];
      console.log(
        '[formatSolutions] Route',
        route.id,
        'polyline:',
        route.polyline?.substring(0, 50),
        'steps:',
        newSteps?.length,
      );
      mapDataTmp[route.driver.id] = {
        polyline: newSteps?.length > 0 ? route.polyline : null,
        steps: newSteps,
        color: color,
        endAddressLat: route.endAddressLat,
        endAddressLng: route.endAddressLng,
        startAddressLat: route.startAddressLat,
        startAddressLng: route.startAddressLng,
      };
      console.log(
        '[formatSolutions] mapData for driver',
        route.driver.id,
        ':',
        mapDataTmp[route.driver.id].polyline?.substring(0, 50),
      );
    });
    setAllStops(allStopsTmp);

    if (selectedStep?.id) {
      setSelectedStep({});
    }

    if (dragDriverIds?.length > 0) {
      setDragDriverIds([]);
    }
    setSolutions(solutionsTmp);
    setOriginalSolutions(solutionsTmp);
    setMapData(mapDataTmp);
    dispatch(setShouldFitbounds(true));
  };

  const onHideDriver = (driverId) => {
    if (dragDriverIds.includes(driverId.toString())) {
      api.open({
        type: 'error',
        content: t('cannotHideWhileDragging'),
        style: { marginTop: '52px' },
      });
      return;
    }
    if (hideDriverIds?.includes(driverId)) {
      setHideDriverIds(hideDriverIds.filter((id) => id !== driverId));
    } else {
      setHideDriverIds([...hideDriverIds, driverId]);
    }
  };

  // Master toggle next to the "Shifts" header. If any shift is still
  // visible → hide everyone. If everyone is hidden → unhide everyone.
  // Same drag-in-progress guard as the per-row toggle, otherwise a bulk
  // toggle mid-drag would yank the row out from under react-dnd.
  const onClickHideAll = () => {
    if (isDragging) {
      messageApi.open({
        type: 'error',
        content: t('cannotHideWhileDragging'),
        style: { marginTop: '52px' },
      });
      return;
    }
    const allDriverIds = (routes || []).map((r) => r.driver?.id).filter((id) => id != null);
    const anyVisible = allDriverIds.some((id) => !hideDriverIds.includes(id));
    setHideDriverIds(anyVisible ? allDriverIds : []);
  };

  // One-click "solo": clicking a driver's avatar isolates them (hide everyone
  // else, which auto-fits the time axis to their route). Clicking it again when
  // they're already the only visible driver restores everyone.
  const onSoloDriver = (driverId) => {
    if (isDragging) {
      messageApi.open({
        type: 'error',
        content: t('cannotHideWhileDragging'),
        style: { marginTop: '52px' },
      });
      return;
    }
    const allDriverIds = (routes || []).map((r) => r.driver?.id).filter((id) => id != null);
    const visible = allDriverIds.filter((id) => !hideDriverIds.includes(id));
    const alreadySolo = visible.length === 1 && visible[0] === driverId;
    setHideDriverIds(alreadySolo ? [] : allDriverIds.filter((id) => id !== driverId));
  };

  const revertDelivery = (driverId) => {
    setSolutions((prev) => {
      const original = prev?.[driverId] || [];
      if (original.length === 0) return prev;

      const next = [...original];
      const pickups = original.filter((s) => s.type === STEP_TYPE.pickup);

      // Scheduled-route steps carry `orderIds` (an array) but no singular
      // `orderId` (see buildNewSteps), so match a delivery to a pickup when they
      // share any order id — works for single and merged (multi-order) steps.
      // `pickup` is passed in (it's the forEach loop variable below, not in scope
      // where this helper is declared).
      const belongsToPickup = (s, pickup) =>
        s.type === STEP_TYPE.delivery && (s.orderIds || []).some((id) => (pickup.orderIds || []).includes(id));

      pickups.forEach((pickup) => {
        const deliveries = original.filter((s) => belongsToPickup(s, pickup));
        if (deliveries.length <= 1) return;

        const deliveryIdxs = original.map((s, i) => (belongsToPickup(s, pickup) ? i : -1)).filter((i) => i >= 0);

        const reversed = [...deliveries].reverse();
        deliveryIdxs.forEach((idx, i) => {
          next[idx] = reversed[i];
        });
      });

      if (next.every((s, i) => s === original[i])) return prev;
      const newDragDriverIds = [...new Set([...dragDriverIds, driverId].filter((id) => id !== 'unscheduled'))];

      setDragDriverIds(newDragDriverIds);
      return { ...prev, [driverId]: next };
    });
  };

  useEffect(() => {
    formatSolutions();
  }, [routes, oneHourPx, orders, date]);

  // OVO-208 — reset undo/redo history whenever the plan reloads from the
  // server (date change / sync / optimise / persist). If the reload is the
  // result of an Optimise, seed one undo step with the operator's pre-optimise
  // manual arrangement so they can restore it.
  useEffect(() => {
    setRedoStack([]);
    if (lastPreOptimizeRef.current) {
      setUndoStack([lastPreOptimizeRef.current]);
      lastPreOptimizeRef.current = null;
    } else {
      setUndoStack([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routes]);

  const onChangeDate = (dateInput) => {
    setDate(dateInput);
    setSearchParams({ date: dateInput.format(DATE_BACKEND_FORMAT) });
    Cookies.set('date', dateInput.format(DATE_BACKEND_FORMAT));
  };

  const onZoomIn = () => {
    if (oneHourPx >= 1500) return;
    setOneHourPx((prev) => prev + 300);
  };

  const onZoomOut = () => {
    if (oneHourPx <= 300) return;
    setOneHourPx((prev) => prev - 300);
  };

  const isDispatchUpdated = useMemo(() => {
    return routes.some((route) => route.dispatched);
  }, [routes]);

  return (
    <Spin tip={t('common:loading')} spinning={isFetching}>
      {contextHolder}
      <div className="RoutesChart">
        <RoutePlanLeft deliveryDate={date} orders={ordersInDate} />
        {!type && (
          <div className="wrapper">
            <div className="select-date">
              <DatePicker
                onChange={onChangeDate}
                value={date}
                format={FULL_DATE_FORMAT}
                allowClear={false}
                size="large"
              />
            </div>
            <div className={'search-stops-map' + (advancedFilterOpen ? ' advanced-open' : '')}>
              {/* OVO-149 — this is a FILTER (hides non-matching stops on the
                  map + scopes the optimize set), not a search; labeled
                  accordingly. The magnifier next to it (SearchStops) is the
                  actual search. */}
              <Input
                allowClear
                placeholder={t('filterStops')}
                size="large"
                onChange={(e) => setSearchStopsMap(e.target.value)}
                value={searchStopsMap}
                suffix={
                  <span className="stop-filter-suffix">
                    <Tooltip
                      title={<span style={{ whiteSpace: 'pre-line' }}>{t('filterStopsHelp')}</span>}
                      placement="bottomRight"
                      overlayInnerStyle={{ width: 320 }}
                    >
                      <QuestionCircleOutlined style={{ color: '#9e9e9e', cursor: 'help' }} />
                    </Tooltip>
                    {/* Advanced — explicit partner / zip pickers (Sep 2026). The
                        badge counts the active advanced criteria so a collapsed
                        panel never hides that a selection is narrowing the plan.
                        NO Tooltip here (Diego, Sep 3): inside the absolutely
                        positioned filter box it opened upward over the input and
                        got clipped. The panel it opens is self-explanatory and
                        the "?" help text documents it; the label stays as the
                        accessible name. */}
                    <Badge count={advancedCriteriaCount} size="small" offset={[2, -2]}>
                      <Button
                        size="small"
                        type="text"
                        aria-label={t('advancedFilter')}
                        className={
                          'stop-filter-advanced-toggle' + (advancedFilterOpen || advancedCriteriaCount ? ' active' : '')
                        }
                        icon={<FilterOutlined />}
                        onClick={() => setAdvancedFilterOpen((v) => !v)}
                        data-testid="stop-filter-advanced-toggle"
                      />
                    </Badge>
                  </span>
                }
              />
              {advancedFilterOpen && (
                <div className="stop-filter-advanced" data-testid="stop-filter-advanced">
                  {/* OVO-132 — a single-tenant instance has one implicit partner. */}
                  {!tenantConfig.isSingleTenant && (
                    <Select
                      mode="multiple"
                      allowClear
                      showSearch
                      className="stop-filter-partner"
                      placeholder={t('allPartners')}
                      value={filterPartnerIds}
                      onChange={setFilterPartnerIds}
                      options={partnerOptions}
                      optionFilterProp="label"
                      optionRender={renderFilterOption}
                      maxTagCount="responsive"
                      getPopupContainer={(node) => node.parentElement}
                    />
                  )}
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    className="stop-filter-zip"
                    placeholder={t('allZips')}
                    value={filterZips}
                    onChange={setFilterZips}
                    options={zipOptions}
                    optionFilterProp="label"
                    optionRender={renderFilterOption}
                    maxTagCount="responsive"
                    getPopupContainer={(node) => node.parentElement}
                  />
                  {advancedCriteriaCount > 0 && (
                    <Button
                      size="small"
                      type="link"
                      className="stop-filter-clear"
                      onClick={() => {
                        setFilterPartnerIds([]);
                        setFilterZips([]);
                      }}
                    >
                      {t('common:clear')}
                    </Button>
                  )}
                </div>
              )}
            </div>
            <SearchStops stops={allStops} onClickStop={onClickStep} />
            <div className="wrapper-map">
              {/* Reset the map zoom to fit all of today's routes + stops. */}
              <Tooltip title={t('resetZoom')}>
                <Button
                  className="map-reset-btn"
                  icon={<AimOutlined />}
                  onClick={() => dispatch(setShouldFitbounds(true))}
                />
              </Tooltip>
              <RouteMap
                vehicles={routes}
                mapData={mapData}
                routes={routes}
                allOrders={orders}
                onClickStep={onClickStep}
                active={selectedStep}
                hideDriverIds={hideDriverIds}
                stopFilter={stopFilter}
              />
            </div>
            {/* Draggable separator handle */}
            <div className="drag-separator" onMouseDown={onSeparatorMouseDown}>
              <div className="drag-separator-line" />
            </div>
            <div className="wrapper-chart" style={{ height: chartHeight }}>
              <div className="ChartContent">
                <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
                  {isFilterActive && (
                    <div className="optimize-filter-banner">
                      <span>
                        {advancedCriteriaCount
                          ? t('ofOrdersMatchFilter', {
                              matched: matchedUnscheduled,
                              total: totalUnscheduled,
                              filter: filterSummary,
                            })
                          : t('ofOrdersMatch', {
                              matched: matchedUnscheduled,
                              total: totalUnscheduled,
                              search: searchStopsMap,
                            })}
                      </span>
                      <CloseOutlined className="clear-filter" onClick={clearStopFilter} />
                    </div>
                  )}
                  <div className="tbody unscheduled" style={{ width: '100%', height: '48px' }}>
                    <UnScheduled
                      zoom={oneHourPx}
                      onZoomIn={onZoomIn}
                      onZoomOut={onZoomOut}
                      steps={solutions['unscheduled']}
                      realSteps={solutions['unscheduled'] || []}
                      stepsHovered={{}}
                      setStepsHovered={() => {}}
                      onHoverStep={() => {}}
                      handleCloseStepPopup={() => {}}
                      isDragging={isDragging}
                      idsHides={[]}
                      onClickStep={onClickStep}
                      onClickHide={() => {}}
                      multipleStepState={multipleStepState}
                      selectedMultipleSteps={selectedMultipleSteps}
                      selectedStep={selectedStep}
                      stopFilter={stopFilter}
                    />
                  </div>
                  <div className="scrollCustom tbody" ref={containerTimelineRef} onMouseDown={onTimelineMouseDown}>
                    <DriverTime
                      hours={hours}
                      oneHourPx={oneHourPx}
                      // Master visibility toggle next to "Shifts". Visible
                      // only when there's at least one shift to act on.
                      hasShifts={(routes || []).length > 0}
                      allHidden={
                        (routes || []).length > 0 &&
                        (routes || [])
                          .map((r) => r.driver?.id)
                          .filter((id) => id != null)
                          .every((id) => hideDriverIds.includes(id))
                      }
                      onClickHideAll={onClickHideAll}
                    />
                    <DriverList
                      isDragging={isDragging}
                      hours={hours}
                      oneHourPx={oneHourPx}
                      minutes={minutes}
                      startTime={startTime}
                      solutions={solutions}
                      routes={routes}
                      idsLock={[]}
                      onClickStep={onClickStep}
                      onClickRoute={() => {}}
                      multipleStepState={multipleStepState}
                      selectedMultipleSteps={selectedMultipleSteps}
                      selectedStep={selectedStep}
                      onClickHide={onHideDriver}
                      onSoloDriver={onSoloDriver}
                      idsHide={hideDriverIds}
                      revertDelivery={revertDelivery}
                    />
                  </div>
                </DragDropContext>
              </div>
            </div>
            <div className="summary-routes">
              <p>
                <b>
                  {summaryRoutes.stepsDone} / {summaryRoutes.stepsTotal}
                </b>{' '}
                {t('stopsCompleted')}
              </p>
              <p>
                <b>
                  {summaryRoutes.stepsSkipped} / {summaryRoutes.stepsTotal}
                </b>{' '}
                {t('stopsSkipped')}
              </p>
              <p>
                <b>
                  {summaryRoutes.stepsFailed} / {summaryRoutes.stepsTotal}
                </b>{' '}
                {t('stopsFailed')}
              </p>
              <p>
                <b>
                  {summaryRoutes.stepsTotal} / {summaryRoutes.totalOrders}
                </b>{' '}
                {t('scheduled')}
              </p>
              <p>
                <b>{summaryRoutes.workingMins.toFixed(2)}</b> {t('workingMins')}
              </p>
              <p>
                <b>{(summaryRoutes.distance / 1000).toFixed(2)}</b> {t('totalKm')}
              </p>
            </div>
            <div className="actions-route-plan">
              <div className="left">
                <Button type="primary" onClick={handleSynchronize}>
                  {t('synchronize')}
                  <ReactSVG className="icon" src="/icons/sync.svg" />
                </Button>
              </div>
              <div className="right">
                {/* OVO-208 — undo/redo the drag-drop arrangement (client-side;
                    restore-only, persist via Apply/Optimise as usual). */}
                <Tooltip title={t('undo')}>
                  <Button
                    onClick={handleUndo}
                    disabled={!undoStack.length}
                    icon={<UndoOutlined />}
                    style={{ marginRight: 4 }}
                  />
                </Tooltip>
                <Tooltip title={t('redo')}>
                  <Button
                    onClick={handleRedo}
                    disabled={!redoStack.length}
                    icon={<RedoOutlined />}
                    style={{ marginRight: 8 }}
                  />
                </Tooltip>
                {dragDriverIds?.length > 0 && (
                  <Button type="primary" ghost onClick={handleReorder}>
                    {t('common:apply')}
                    <ReactSVG className="icon" src="/icons/check.svg" />
                  </Button>
                )}
                {!dragDriverIds?.length && (
                  <Tooltip title={t('optimizeHint')}>
                    <Button type="primary" onClick={() => handleOptimizeGmpro()} data-testid="day-plan-optimize">
                      {isFilterActive
                        ? t('optimizeFiltered', { matched: matchedUnscheduled, total: totalUnscheduled })
                        : t('optimize')}
                      <ReactSVG className="icon" src="/icons/sync.svg" />
                    </Button>
                  </Tooltip>
                )}
                {!dragDriverIds?.length && (
                  <Popconfirm
                    title={t('reoptimizeAllTitle')}
                    description={<div style={{ maxWidth: 340 }}>{t('reoptimizeAllDesc')}</div>}
                    onConfirm={() => handleOptimizeGmpro({ reoptimizeAll: true })}
                    okText={t('reoptimizeAllOk')}
                    cancelText={t('common:cancel')}
                  >
                    <Button style={{ marginLeft: 4 }} data-testid="day-plan-reoptimize-all">
                      {t('reoptimizeAll')}
                    </Button>
                  </Popconfirm>
                )}
                {/* <Button type="primary" onClick={handleOptimize}>
                  Optimize Route
                  <ReactSVG className="icon" src="/icons/sync.svg" />
                </Button> */}
                <Button
                  size="large"
                  className={`btn-dispatch-route ${isDispatchUpdated ? 'btn-dispatch-updated' : ''}`}
                  onClick={handleDispatch}
                >
                  {t('dispatchToDrivers')}
                  <ReactSVG className="icon" src="/icons/dispatch-to-driver.svg" />
                </Button>
              </div>
            </div>
          </div>
        )}
        {(type === 'add-orders' || type === 'orders') && (
          <div className="wrapper-orders">
            <h2 className="title">{type === 'add-orders' ? t('addOrders') : t('orders')}</h2>
            {/* OVO-026 — toolbar wired client-side: search box + filter drawer
                + column-config gear. The data fetch above this stays date-
                scoped via the URL; filtering reduces the in-memory array. */}
            {(() => {
              const includes = (haystack, needle) =>
                (haystack || '')
                  .toString()
                  .toLowerCase()
                  .includes((needle || '').toLowerCase());
              const baseFiltered = orders.filter((o) =>
                type === 'orders'
                  ? ![ORDER_STATUSES.DRAFT, ORDER_STATUSES.GEOCODING, ORDER_STATUSES.ERROR].includes(o.status)
                  : o.status === 'DRAFT',
              );
              const q = (routeOrdersSearch || '').trim();
              const fq = routeOrdersFilterQuery || {};
              const itemTypeIdList = (() => {
                if (Array.isArray(fq.partnerItemTypeIds)) return fq.partnerItemTypeIds;
                if (typeof fq.partnerItemTypeIds === 'string' && fq.partnerItemTypeIds.length > 0) {
                  return fq.partnerItemTypeIds.split(',');
                }
                return [];
              })();
              const issueTypeList = Array.isArray(fq.issueTypes)
                ? fq.issueTypes
                : (fq.issueTypes || '').split(',').filter(Boolean);
              const matchesSearch = (o) => {
                if (!q) return true;
                return (
                  includes(o.name, q) ||
                  includes(o.pickupName, q) ||
                  includes(o.deliveryAddress, q) ||
                  includes(o.pickupAddress, q) ||
                  includes(o.orderCode, q) ||
                  includes(o.orderRef, q) ||
                  includes(o.partnerOrderRef, q) ||
                  includes(o.partner, q) ||
                  includes(o.partnerRef?.name, q)
                );
              };
              const matchesFilter = (o) => {
                if (fq.name && !includes(o.name, fq.name)) return false;
                if (fq.pickupName && !includes(o.pickupName, fq.pickupName)) return false;
                if (fq.partnerOrderRef && !includes(o.partnerOrderRef, fq.partnerOrderRef)) return false;
                if (fq.orderRef && !includes(o.orderRef, fq.orderRef)) return false;
                if (fq.routeId && String(o.routeId ?? '') !== String(fq.routeId)) return false;
                if (fq.status && o.status !== fq.status) return false;
                if (fq.partnerId && String(o.partnerRef?.id ?? o.partnerId ?? '') !== String(fq.partnerId))
                  return false;
                if (fq.orderType && o.orderType !== fq.orderType) return false;
                if (issueTypeList.length > 0 && !issueTypeList.includes(o.issueType)) return false;
                if (itemTypeIdList.length > 0) {
                  const orderItemTypeIds = (o.orderProducts || []).map((p) =>
                    String(p.partnerItemTypeId ?? p.partnerItemType?.id ?? ''),
                  );
                  if (!itemTypeIdList.some((id) => orderItemTypeIds.includes(String(id)))) return false;
                }
                return true;
              };
              const displayedOrders = baseFiltered.filter((o) => matchesSearch(o) && matchesFilter(o));
              const activeFilterCount = [
                fq.name,
                fq.pickupName,
                fq.partnerOrderRef,
                fq.orderRef,
                fq.routeId,
                fq.status,
                fq.partnerId,
                fq.orderType,
                issueTypeList.length > 0 ? '1' : '',
                itemTypeIdList.length > 0 ? '1' : '',
              ].filter(Boolean).length;
              // OVO-026 — per-row dot menu (delete) mirrors OrdersTable's
              // withAction. Only rendered when the order is in a deletable
              // state, otherwise the icon stays disabled — same predicate
              // as /admin/orders, except UNASSIGNED is excluded since the
              // user typically wants Unassign (the bulk-action option)
              // rather than outright delete on the route-plan flow.
              const DELETABLE_STATUSES_RP = [ORDER_STATUSES.DRAFT, ORDER_STATUSES.GEOCODING, ORDER_STATUSES.ERROR];
              const handleDeleteRow = async (record) => {
                try {
                  await dispatch(doDeleteOrders({ orderCodes: [record.orderCode] })).unwrap();
                  api.success({ content: t('orderDeleted', { orderCode: record.orderCode, ns: 'orders' }) });
                  await getAllDataForPage();
                } catch (e) {
                  api.error({ content: e?.message || t('failedToDelete', { ns: 'orders' }) });
                }
              };
              const withRowAction = (record) => {
                const isDeletable = DELETABLE_STATUSES_RP.includes(record.status);
                return (
                  <div className="actions">
                    {isDeletable ? (
                      <Popconfirm
                        title={t('deleteOrder', { ns: 'orders' })}
                        description={t('deleteOrderConfirm', { orderCode: record.orderCode, ns: 'orders' })}
                        onConfirm={() => handleDeleteRow(record)}
                        okText={t('common:delete')}
                        cancelText={t('common:cancel')}
                        okButtonProps={{ danger: true }}
                      >
                        <Button type="link" className="more">
                          <ReactSVG className="icon" src="/icons/more.svg" />
                        </Button>
                      </Popconfirm>
                    ) : (
                      <Button type="link" className="more" disabled>
                        <ReactSVG className="icon" src="/icons/more.svg" />
                      </Button>
                    )}
                  </div>
                );
              };
              // Bulk-action popover content (orders subview only). Two
              // destructive actions on selection — Remove (→ DRAFT, stays
              // for the date) and Unassign (→ UNASSIGNED, leaves the date).
              const popoverContent = (
                <div className="buttons" style={{ display: 'flex', gap: 4 }}>
                  <Button type="link" danger onClick={changeOrdersToDraft}>
                    {t('common:remove')}
                  </Button>
                  <Button type="link" danger onClick={changeOrdersToUnassigned}>
                    {t('unassign')}
                  </Button>
                </div>
              );
              return (
                <>
                  <div className="riders-header-table page-header-table">
                    <div className="search">
                      <Input
                        placeholder={t('common:search')}
                        value={routeOrdersSearchInput}
                        onChange={(e) => setRouteOrdersSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') setRouteOrdersSearch(routeOrdersSearchInput);
                        }}
                        onBlur={() => setRouteOrdersSearch(routeOrdersSearchInput)}
                        suffix={<img src="/icons/search.svg" />}
                      />
                    </div>
                    <div className="buttons">
                      {/* Bulk action popover — orders subview only. Appears
                          when at least one row is selected. Mirrors the
                          /admin/orders Action popover. */}
                      {type === 'orders' && ordersSelected.length > 0 && (
                        <Popover
                          open={isRouteOrdersActionOpen}
                          content={popoverContent}
                          overlayClassName="OrdersPagePopoverAction"
                          placement="bottom"
                        >
                          <Button
                            className="FilterButton ButtonFilter"
                            onClick={() => setIsRouteOrdersActionOpen((o) => !o)}
                          >
                            <div className="count">{ordersSelected.length}</div>
                            {t('common:action')}
                            <ReactSVG className="icon" src="/icons/arrow_drop_down.svg" />
                          </Button>
                        </Popover>
                      )}
                      {/* Primary action first — `orders` view navigates to
                          the add-orders subview; `add-orders` view commits
                          the selected DRAFT orders to the date (needs at
                          least one selection). */}
                      {type === 'orders' && (
                        <Link to={`/admin/route-plans/add-orders?date=${date?.format(DATE_BACKEND_FORMAT)}`}>
                          <Button type="primary">
                            {t('addOrders')}
                            <ReactSVG className="icon" src="/icons/plus.svg" />
                          </Button>
                        </Link>
                      )}
                      {type === 'add-orders' && (
                        <Button
                          type="primary"
                          onClick={changeOrdersToUnassigned}
                          disabled={ordersSelected.length === 0}
                        >
                          {t('addOrdersToDate')}
                          <ReactSVG className="icon" src="/icons/plus.svg" />
                        </Button>
                      )}
                      <Badge count={activeFilterCount} size="small" offset={[-5, 0]}>
                        <Button className="FilterButton ButtonFilter" onClick={() => setIsRouteOrdersFilterOpen(true)}>
                          {t('common:filter')}
                          <ReactSVG className="icon" src="/icons/filter_list.svg" />
                        </Button>
                      </Badge>
                      <Button
                        type="text"
                        onClick={() => routeOrdersConfigRef.current?.openDrawer?.()}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <ReactSVG className="icon" src="/icons/settings.svg" />
                      </Button>
                    </div>
                  </div>
                  <OrdersFilter
                    hideDriver
                    open={isRouteOrdersFilterOpen}
                    onClose={() => setIsRouteOrdersFilterOpen(false)}
                    query={routeOrdersFilterQuery}
                    onApply={(params) => {
                      // The drawer's Apply hands back the union of all field
                      // states (including the empty ones). Strip the empties
                      // so the active-filter count stays accurate.
                      const cleaned = {};
                      Object.entries(params || {}).forEach(([k, v]) => {
                        if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) return;
                        cleaned[k] = v;
                      });
                      setRouteOrdersFilterQuery(cleaned);
                      setIsRouteOrdersFilterOpen(false);
                    }}
                    hideDateRange
                  />
                  <div className="content">
                    <div className="left">
                      <RouteOrdersTable
                        solutions={solutions}
                        routes={routes}
                        showIndexVisit={type === 'orders'}
                        dataSource={displayedOrders}
                        selectedRowKeys={ordersSelected}
                        onSelectChange={onSelectChangeAddOrders}
                        rowKey="orderCode"
                        configRef={routeOrdersConfigRef}
                        withAction={withRowAction}
                      />
                    </div>
                    <div className="right"></div>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>
      {rubberRect && (
        <div
          className="rubber-select-rect"
          style={{
            left: rubberRect.left,
            top: rubberRect.top,
            width: rubberRect.width,
            height: rubberRect.height,
          }}
        />
      )}
      {multipleStepState.steps?.length > 0 && !rubberRect && (
        <div className="multi-select-bar">
          <span className="count">{t('multiSelect.selected', { count: multipleStepState.steps.length })}</span>
          {selectedScheduledSteps.length > 0 && (
            <Popconfirm
              title={t('multiSelect.unassignConfirm', { count: selectedOrderCodes.length })}
              onConfirm={handleBulkUnassign}
              okText={t('common:yes')}
              cancelText={t('common:no')}
            >
              <Button danger size="small">
                {t('multiSelect.unassignAll')}
              </Button>
            </Popconfirm>
          )}
          <Button size="small" type="primary" onClick={() => setSendToDriverOpen(true)}>
            {t('multiSelect.sendToDriver')}
          </Button>
          <Button size="small" onClick={clearMultiSelection}>
            {t('multiSelect.clear')}
          </Button>
        </div>
      )}
      <Modal
        title={t('multiSelect.sendToDriverTitle', { count: multipleStepState.steps?.length || 0 })}
        open={sendToDriverOpen}
        onCancel={() => setSendToDriverOpen(false)}
        onOk={() => handleBulkSendToDriver()}
        okText={t('multiSelect.send')}
        okButtonProps={{ disabled: !sendTargetRouteId, loading: sendingToDriver }}
        destroyOnClose
      >
        <p>{t('multiSelect.sendToDriverHint')}</p>
        <Select
          style={{ width: '100%' }}
          placeholder={t('multiSelect.sendToDriverSelect')}
          value={sendTargetRouteId}
          onChange={setSendTargetRouteId}
          options={sendCandidateRoutes.map((r) => ({
            value: r.id,
            label: `${r.driver?.name || '—'} · ${r.shiftStart || ''}-${r.shiftEnd || ''} · #${r.id}`,
          }))}
        />
      </Modal>
      <SwapStopsToDriver
        state={multipleStepState}
        setMultipleStepState={setMultipleStepState}
        handleOk={moveSteps}
        handleCancel={handleCancelMultiStops}
        routes={routes}
        solutions={solutions}
        orders={ordersInDate || []}
      />

      {selectedStep.id && (
        <StopClicked
          item={selectedStep}
          solutions={solutions}
          routes={routes}
          onStepClick={setSelectedStep}
          allOrders={ordersInDate || []}
          onUnassignStop={handleUnassignStop}
          onCancelStop={handleCancelStop}
        />
      )}
    </Spin>
  );
};
export default RoutesChart;
